// src/components/ChatContainer.tsx
import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import MessageBubble from "./ui/MessageBubble";
import TypingIndicator from "./ui/TypingIndicator";
import TimerBubble, { type TimerBubbleHandle } from "./ui/TimerBubble";
import StepUploadBubble from "./ui/StepUploadBubble";
import StepHtmlActivityBubble from "./ui/StepHtmlActivityBubble";
import CoachSkillWidget, {
  type CoachSkillWidgetHandle,
} from "./ui/CoachSkillWidget";
import StepExpectancyTrackerBubble from "./ui/StepExpectancyTrackerBubble";
import StepAbcGameBubble from "./ui/StepAbcGameBubble";
import StepImaginaryHandwashingBubble from "./ui/StepImaginaryHandwashingBubble";

import StepCopingBuddyBubble, {
  type StepCopingBuddyBubbleHandle,
} from "./copingBuddy/StepCopingBuddyBubble";

import StepTargetGameBubble from "./targetGame/StepTargetGameBubble";
import StepLearningBuddyBubble, {
  type StepLearningBuddyBubbleHandle,
} from "./learningBuddy/StepLearningBuddyBubble";

import ChatInput, { type ChatInputHandle } from "./ChatInput";
import ChatHeader from "../ChatHeader";
import BadgeRow from "./ui/BadgeRow";
import BadgeCollectionRow from "./ui/BadgeCollectionRow";

import { Message, ArtifactConfig } from "../types/chat";
import { N8N_INIT_URL, N8N_WEBHOOK_URL } from "../config/n8n";
import {
  DATABASE_ROUTING_CONFIGURED,
  RUNTIME_DB,
  type RuntimeDb,
} from "../config/runtimeDb";
import WelcomeCard from "./ui/WelcomeCard";
import HomeworkSummaryRow from "./ui/HomeworkSummaryRow";
import PracticeCalendarModal from "./ui/PracticeCalendarModal";
import { SquarePen } from "lucide-react";
import { fireConfetti } from "../utils/confetti";
import QuickActionsBar from "./ui/QuickActionsBar";
import type { GptLiveInputContext } from "../utils/gptLiveInputRouter";

import SudsInput, {
  type SudsInputSource,
} from "./ui/SudsInput";

import { extractQuickActionsFromText, QuickAction } from "../utils/quickActions";
import { getHomeworkVoiceChoices, parseHomeworkVoiceSelection } from "../utils/homeworkVoiceSelection";
import { isSudsPrompt } from "../utils/isSudsPrompt";
import { useInactivityWatchdog } from "../hooks/useInactivityWatchdog";
import { ensureAudioContext } from "../lib/sound";
import {
  useGptLiveVoice,
  type GptLiveClientDelegation,
} from "../hooks/useGptLiveVoice";

import { useRealtimeVoice } from "../hooks/useRealtimeVoice";
import {
  DEFAULT_VOICE_KEY,
  VOICES,
  VOICE_BASE64,
  VoiceKey,
} from "../config/voices";

import RewardOverlay from "./ui/RewardOverlay";


function requireWebhookUrl(): string {
  const url = N8N_WEBHOOK_URL?.trim();

  if (!url) {
    throw new Error("N8N_WEBHOOK_URL is not configured.");
  }

  return url;
}

//const DEBUG = process.env.NODE_ENV !== "production";
const DEBUG =
  (process.env.REACT_APP_DEBUG || "").toLowerCase() === "true";

const debugLog = (...args: any[]) => {
  if (DEBUG) console.log(...args);
};

type DeviceType =
  | "iphone"
  | "android_phone"
  | "ipad"
  | "android_tablet"
  | "desktop_laptop"
  | "unknown";

const detectDeviceType = (): DeviceType => {
  if (typeof navigator === "undefined") return "unknown";

  const userAgent = navigator.userAgent || "";
  const platform = navigator.platform || "";
  const maxTouchPoints = navigator.maxTouchPoints || 0;

  // iPadOS may identify itself as a Mac, so check touch capability too.
  const isIPad =
    /iPad/i.test(userAgent) ||
    (platform === "MacIntel" && maxTouchPoints > 1);

  if (isIPad) return "ipad";
  if (/iPhone|iPod/i.test(userAgent)) return "iphone";

  if (/Android/i.test(userAgent)) {
    return /Mobile/i.test(userAgent)
      ? "android_phone"
      : "android_tablet";
  }

  if (/Windows|Macintosh|MacIntel|Linux|CrOS/i.test(
    `${userAgent} ${platform}`
  )) {
    return "desktop_laptop";
  }

  return "unknown";
};

// 🩵 iOS Safari fix: proactively resume AudioContext on first user gesture
if (typeof window !== "undefined") {
  const unlock = async () => {
    try {
      const mod = await import("../lib/sound");
      await mod.ensureAudioContext?.();
    } catch (e) {
      if (DEBUG) console.warn("[Audio unlock] failed:", e);
    }
    document.removeEventListener("touchstart", unlock);
    document.removeEventListener("click", unlock);
  };
  document.addEventListener("touchstart", unlock, { once: true });
  document.addEventListener("click", unlock, { once: true });
}

// 🔊 Realtime Voice (feature flag)
const ENABLE_REALTIME_VOICE =
  (process.env.REACT_APP_REALTIME_VOICE || "").toLowerCase() === "true";

const GPT_LIVE_HOSTNAME =
  typeof window !== "undefined"
    ? window.location.hostname
    : "";

const GPT_LIVE_QUERY_FLAG =
  typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("gpt-live-1")
    : null;

const GPT_LIVE_QUERY_ENABLED =
  GPT_LIVE_QUERY_FLAG?.toLowerCase() === "true";

const GPT_LIVE_ALLOWED_HOST =
  GPT_LIVE_HOSTNAME === "assistant-dev.educationforall.ai" ||
  GPT_LIVE_HOSTNAME === "assistant.educationforall.ai";

// Explicit URL override for legacy real-time voice.
const LEGACY_VOICE_QUERY_FLAG =
  typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("legacy-voice")
    : null;

const LEGACY_VOICE_QUERY_ENABLED =
  LEGACY_VOICE_QUERY_FLAG?.toLowerCase() === "true";

// GPT-Live is the default in development.
// An explicit legacy override always takes precedence.
// Production remains unchanged.
const ENABLE_GPT_LIVE_1 =
  GPT_LIVE_ALLOWED_HOST &&
  !LEGACY_VOICE_QUERY_ENABLED;

const SELECTED_VOICE_PIPELINE =
  ENABLE_GPT_LIVE_1
    ? "gpt-live-1"
    : "serial-stt-n8n-elevenlabs";

// 🔊 TTS volume (0.0 = mute, 1.0 = max)
const SYSTEM_TTS_VOLUME = 0.2;

const REQUEST_TEXT_KEY = "chatInput" as const;
const AUDIO_TOGGLE_KEY = "audioPlayback" as const;
const ALT_AUDIO_TOGGLE_KEY = "efachat.audioPlayback" as const;
const AIRTABLE_PATIENT_RECORD_ID_KEY = "airtablePatientRecordId" as const;
const AIRTABLE_PATIENT_RECORD_ID_ACCESS_CODE_KEY =
  "airtablePatientRecordIdAccessCode" as const;

const REWARD_MS_TEXT = 6500;
const REWARD_MS_AUDIO = 12500; // tweak

// ✅ Mobile fallback: show reward GIF inline in chat (iOS/Android)
const REWARD_GIF_SRC = "/assets/rewards/capybara_reward_1p8s_360w_15fps.gif";
const REWARD_GIF_WIDTH_CLASS = "w-[220px] sm:w-[260px]";

type SelectedHomework = { recordId: string;[k: string]: any };

type HomeworkSummaryBucket = {
  count: number;
  label: string;
  subtitle: string;
  show: boolean;
  pastDueCount?: number;
  dueTodayCount?: number;
  dueSoonCount?: number;
};

type HomeworkSummary = {
  needsAttention?: HomeworkSummaryBucket;
  openHomework?: HomeworkSummaryBucket;
  totalIncompleteCount?: number;
  updatedAt?: string;
};

export type PracticeCalendarHomework = {
  recordId: string | null;
  activityName: string | null;
  activityType: string | null;
  completionStatus: string | null;
  dueDate: string | null;
  completedAttemptedDate: string | null;

  suds1?: number | null;
  suds2?: number | null;
  points?: number | null;
  coins?: number | null;

  calendarDate: string | null;
  eventType:
  | "completed"
  | "attempted"
  | "missed"
  | "due"
  | "incomplete"
  | null;
  isPastDue: boolean;
  isDueToday: boolean;
};

type ChatAttachment = {
  type: string;
  label: string;
  filename: string;
  mimeType: string;
  size: number | null;
  downloadUrl: string;
  helperText?: string;
};

type MessageWithArtifact = Message & {
  artifact?: ArtifactConfig | null;
  attachments?: ChatAttachment[];
  mode?: string | null;

  // 🧭 Router / Orchestrator extras (safe optional additions)
  intent?: string;
  hideQuick?: boolean;
  signals?: Record<string, any>;
  isQAStep?: boolean;

  coachPrompts?: string[];
  coachSkill?: string | null;

  // ✅ Rewards
  pointsEarned?: number;
  coinsEarned?: number;

  // ✅ Mobile reward fallback: render GIF row directly in chat
  rewardGif?: boolean;
};

type CoachSkillActivityEvent = {
  widgetType: "coping_buddy" | "learning_buddy";
  widgetLabel: "Coping Buddy" | "Learning Buddy";
  activityKey: string;
  activityTitle: string;
  eventType: "started" | "completed" | "used" | "saved" | "submitted";
  metadata?: Record<string, unknown>;
};

const WORD_TO_DIGIT: Record<string, string> = {
  zero: "0", one: "1", two: "2", three: "3", four: "4",
  five: "5", six: "6", seven: "7", eight: "8", nine: "9", ten: "10",
};

// 🔢 Safe numeric coercion (used for rewards)
const toNumber = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const normalizeSuds = (s: string) => {
  const raw = (s || "").trim();
  const lower = raw.toLowerCase();

  // typed-safe: exact word mapping only
  if (WORD_TO_DIGIT[lower]) return WORD_TO_DIGIT[lower];

  return raw;
};

// ✅ Voice-only normalization (do NOT use for typed input)
const VOICE_SINGLE_WORD_DIGIT: Record<string, string> = {
  // common homophones/mistranscriptions
  won: "1",
  to: "2",
  too: "2",
  tu: "2",
  who: "2",
  for: "4",
  fore: "4",
  ate: "8",
  hey: "8",
  nein: "9",
  n: "10",
  en: "10",
  den: "10",
  hen: "10",
  ken: "10",
  then: "10",
  tan: "10",

  // normal number words
  zero: "0",
  one: "1",
  two: "2",
  three: "3",
  four: "4",
  five: "5",
  six: "6",
  seven: "7",
  eight: "8",
  nine: "9",
  ten: "10",
};

// Returns "0".."10" for a single-word voice transcript, otherwise null.
// Examples: "Five." -> "5", "tu" -> "2", "7" -> "7"
function normalizeVoiceSingleWordDigit(raw: string): string | null {
  const cleaned = raw
    .trim()
    .toLowerCase()
    .replace(/^[^\w]+|[^\w]+$/g, ""); // strip leading/trailing punctuation

  if (!cleaned) return null;

  // numeric already
  if (/^\d{1,2}$/.test(cleaned)) {
    const n = Number(cleaned);
    if (Number.isFinite(n) && n >= 0 && n <= 10) return String(n);
  }

  return VOICE_SINGLE_WORD_DIGIT[cleaned] ?? null;
}

// Emoji SUDS voice shortcuts.
// These are used ONLY when the configured SUDS control is "emoji".
//
// Client says:
//   "low"      → internal SUDS 2
//   "moderate" → internal SUDS 5
//   "high"     → internal SUDS 8
function normalizeVoiceEmojiSudsCategory(
  raw: string
): string | null {
  const cleaned = (raw || "")
    .trim()
    .toLowerCase()
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!cleaned) return null;

  if (
    cleaned === "low" ||
    cleaned === "low distress"
  ) {
    return "2";
  }

  if (
    cleaned === "moderate" ||
    cleaned === "moderate distress"
  ) {
    return "5";
  }

  if (
    cleaned === "high" ||
    cleaned === "high distress"
  ) {
    return "8";
  }

  return null;
}

const isSudsGroup = (actions: QuickAction[]) => {
  if (!actions || actions.length === 0) return false;
  const vals = actions.map((a) => a.value.trim());
  const allNumeric = vals.every((v) => /^(?:[0-9]|10)$/.test(v));
  return allNumeric && vals.length >= 5;
};

// ─────────────────────────────────────────────────────────────
// FastTrack-aligned coping query phrases (must match OCD v4)
// ─────────────────────────────────────────────────────────────
const COPING_QUERY_PHRASES = new Set([
  "coping skill",
  "coping skills",
  "coping strategy",
  "coping strategies",
  "my coping skill",
  "my coping skills",
  "my coping strategy",
  "my coping strategies",
  "my favorite coping skills",
  "my favorite coping strategies",
  "what are my coping skills",
  "what are my coping strategies",
  "show me coping skills",
  "show me a coping skill"
]);

const normalizeForIntent = (input: string): string =>
  input
    .toLowerCase()
    .replace(/[?!.]/g, "")
    .replace(/\s+/g, " ")
    .trim();


//
// GPT-LIVE: SAFE LOCAL COPING-SKILL VOICE NORMALIZATION
//
// Only recognized local UI commands are rewritten.
// Freeform game answers, clinical responses, and normal
// conversation retain their original transcripts.
//
const LOCAL_COPING_UI_COMMANDS = new Set([
  // Breathing and animation controls
  "start",
  "start breathing",
  "pause",
  "pause breathing",
  "resume",
  "resume breathing",
  "reset",
  "reset breathing",

  // Game controls
  "hint",
  "give me a hint",
  "clue",
  "help",
  "pass",

  "start over",
  "restart",
  "restart game",
  "restart the game",
  "new game",
  "start a new game",
  "play again",

  "quit",
  "quit game",
  "quit the game",
  "exit game",
  "stop game",
  "end game",
  "done playing",

  // Target Game reveal confirmation
  "reveal item",
  "reveal the item",
  "show answer",
  "reveal answer",
  "yes reveal answer",
  "keep guessing",
  "no keep guessing",
  "yes",
  "no",
]);

function normalizeLocalCopingUiCommand(
  raw: string
): string | null {
  let command = String(raw || "")
    .normalize("NFKC")
    .toLowerCase()
    .replace(/[’]/g, "'")
    .replace(/\[[^\]]*\]/g, " ")
    .replace(/[.,!?;:]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  // Remove a limited number of conversational prefixes.
  for (let i = 0; i < 3; i++) {
    const next = command
      .replace(
        /^(?:okay|ok|alright|all right|well|sure|please|let's|lets|can you|could you|would you)\s+/,
        ""
      )
      .trim();

    if (next === command) break;
    command = next;
  }

  // Optional trailing politeness.
  command = command
    .replace(/\s+please$/, "")
    .trim();

  // Strict allowlist: never return a modified
  // transcript unless it is a recognized UI command.
  return LOCAL_COPING_UI_COMMANDS.has(command)
    ? command
    : null;
}

// Remove recognized non-speech audio annotations
// from the beginning of GPT-Live transcripts.
// Preserve the user's actual spoken words.
function stripLeadingGptLiveAudioTags(
  raw: string
): string {
  return String(raw || "")
    .replace(
      /^(?:\s*\[(?:lip[\s-]*smack|mouth[\s-]*click|inhale|exhale|sigh|breath|breathing|cough|throat[\s-]*clear)\]\s*)+/gi,
      ""
    )
    .trim();
}

// For card selection, remove only generic opening words.
// Keep phrases such as "let's do Butterfly Breath" intact,
// since Coping Buddy already recognizes them.
function stripLocalSelectionLeadIn(
  raw: string
): string {
  return String(raw || "")
    .trim()
    .replace(
      /^(?:(?:okay|ok|alright|all right|well|sure)[\s,.:;!-]+)+/i,
      ""
    )
    .trim();
}

// GPT-Live speech should not read URLs aloud.
// Keep the original text unchanged for the visual chat UI.
const sanitizeForGptLiveSpeech = (value: string): string =>
  String(value ?? "")

    // Pronounce SUDS as "suds", not S-U-D-S.
    // This changes spoken text only, not the chat display.
    .replace(/\bSUDS\b/g, "suds")

    // Remove markdown images entirely.
    .replace(/!\[[^\]]*]\([^)]+\)/g, "")

    // Preserve markdown link label but remove its URL:
    // [Watch the video](https://...) -> Watch the video
    .replace(
      /\[([^\]]+)\]\(https?:\/\/[^)]+\)/gi,
      "$1"
    )

    // Remove bare URLs.
    .replace(/https?:\/\/\S+/gi, "")

    // Tidy whitespace left behind.
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/[ \t]{2,}/g, " ")
    .trim();

function InitOverlay() {
  return (
    <div className="fixed inset-x-0 top-14 z-40 pointer-events-none">
      <div className="mx-4">
        <div className="p-3 rounded-lg border bg-white shadow-sm">
          <div className="animate-pulse space-y-3">
            <div className="h-5 w-40 bg-gray-200 rounded" />
            <div className="h-4 w-3/4 bg-gray-200 rounded" />
            <div className="h-4 w-2/3 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    </div>
  );
}

function CoachingTipsBubble({ prompts }: { prompts: string[] }) {
  if (!Array.isArray(prompts) || prompts.length === 0) return null;

  return (
    <div className="mt-3">
      <div className="rounded-xl border border-green-200 bg-green-50 px-5 py-4 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="text-lg leading-none">💡</div>
          <div>
            <div className="font-semibold text-gray-900">
              Here are some coaching tips to keep in mind:
            </div>
            <ul className="mt-2 list-disc pl-5 text-gray-800 space-y-1">
              {prompts.map((p, i) => (
                <li key={i}>{p}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

const ChatContainer: React.FC = () => {
  const deviceType = useMemo(() => detectDeviceType(), []);

  useEffect(() => {
    const diagnostic = {
      hostname: GPT_LIVE_HOSTNAME,
      queryFlag: GPT_LIVE_QUERY_FLAG,
      queryEnabled: GPT_LIVE_QUERY_ENABLED,
      allowedHost: GPT_LIVE_ALLOWED_HOST,
      gptLiveEnabled: ENABLE_GPT_LIVE_1,
      selectedVoicePipeline: SELECTED_VOICE_PIPELINE,
    };

    console.info("[Voice Pipeline]", diagnostic);
  }, []);

  const [showRewardOverlay, setShowRewardOverlay] = useState(false);
  const lastCelebrationSigRef = useRef<string | null>(null);

  // Lock the inner chat scroller while an HTML activity is maximized.
  const [htmlActivityExpanded, setHtmlActivityExpanded] = useState(false);

  // ✅ Mobile detection (simple + good enough for iOS/Android Chrome/Safari)
  const isMobile = useMemo(() => {
    if (typeof navigator === "undefined") return false;
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  }, []);

  // ✅ Inline reward (mobile): prevent replay on rerender/history
  const lastInlineRewardSigRef = useRef<string | null>(null);

  // ✅ After: const lastInlineRewardSigRef = useRef<string | null>(null);
  const pendingRewardScrollRef = useRef(false);

  // ✅ Inline GIF fallback should run only on mobile
  const shouldUseInlineRewardGif = useCallback(() => {
    return isMobile;
  }, [isMobile]);

  const [pendingAwaitKey, setPendingAwaitKey] = useState<string | null>(null);
  // 🧭 Transient orchestration signals from OCD v4 (opaque to UI)
  const [pendingSignals, setPendingSignals] = useState<Record<string, any> | null>(null);

  const [nickname, setNickname] = useState<string | null>(null);
  const [homeworkSummary, setHomeworkSummary] = useState<HomeworkSummary | null>(null);

  const [practiceCalendarHomework, setPracticeCalendarHomework] =
    useState<PracticeCalendarHomework[]>([]);

  const [showPracticeCalendar, setShowPracticeCalendar] =
    useState(false);

  const [messages, setMessages] = useState<MessageWithArtifact[]>([]);

  // The newest Coach Skill remains voice-active until the user sends a
  // non-widget response through the regular conversation flow.
  //
  // Local widget commands do not create user messages, so Start/Pause/Resume/Reset
  // can continue controlling the widget. Statements such as "I'm done", "next",
  // or "what do I do now" enter the regular flow and deactivate the old widget.
  const activeCoachSkillMessageId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const message = messages[i];

      // A regular user response after the widget ends its local voice-control turn.
      if (message.sender === "user") return null;

      if (
        message.sender === "assistant" &&
        message.intent === "COPING_GUIDE" &&
        message.mode === "GUIDE" &&
        message.coachSkill
      ) {
        return message.id;
      }
    }

    return null;
  }, [messages]);

  // Hide dashboard stats on all screen sizes after the user starts a conversation.
  const hasConversationStarted = messages.some(
    (message) => message.sender === "user"
  );

  const showDashboard = !hasConversationStarted;

  const showMobileDashboard = !isMobile || !hasConversationStarted;

  const [isLoading, setIsLoading] = useState(false);
  // 🧩 Temporarily hide quick replies during timer-to-agent transitions
  const [suppressQuickReplies, setSuppressQuickReplies] = useState(false);

  const [initComplete, setInitComplete] = useState(false);
  const [runtimeDb, setRuntimeDb] = useState<RuntimeDb>(RUNTIME_DB);

  // 🔒 Lock input until init succeeds with a valid access code
  const [accessCodeValid, setAccessCodeValid] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);


  const [badgeLevel, setBadgeLevel] = useState<
    "Youngling" | "Padawan" | "Knight" | "Master" | "Blackbelt" | "Bluebelt" | "Greenbelt" | "Redbelt" | "Yellowbelt" | null
  >(null);
  const [badgeCollection, setBadgeCollection] = useState<string[]>([]);
  const [pointsEarned, setPointsEarned] = useState<number>(0);

  // Wardrobe + user id for Capybara Studio
  const [coins, setCoins] = useState<number>(0);
  const [unlockedItems, setUnlockedItems] = useState<string[]>([]);
  const [equippedItems, setEquippedItems] = useState<string[]>([]);
  const [profileUserId, setProfileUserId] = useState<string>("unknown-user");

  // Avatar ID — stored in backend session_meta + localStorage
  const [avatarId, setAvatarId] = useState<string | null>(
    localStorage.getItem("avatarId")
  );

  // keep localStorage in sync
  useEffect(() => {
    if (avatarId && avatarId.trim() !== "") {
      localStorage.setItem("avatarId", avatarId);
    } else {
      localStorage.removeItem("avatarId");
    }
  }, [avatarId]);

  const [showWelcome, setShowWelcome] = useState(true);
  const [prefillText, setPrefillText] = useState<string | null>(null);
  const [, setSelectedHomework] = useState<SelectedHomework | null>(null);

  // Track whether the user is currently in a homework flow
  const [inHomework, setInHomework] = useState(false);

  const [selfInitiatedPermitted, setSelfInitiatedPermitted] = useState<boolean | null>(null);

  // ---- Realtime voice: merge consecutive final transcripts ----
  const voiceFinalBufferRef = useRef<string>("");
  const voiceSendTimerRef = useRef<number | null>(null);

  // 🧷 Always-current timer-running flag (prevents “late flush” sends)
  const hasVisibleTimerRef = useRef(false);
  const gptLiveInputContextRef = useRef<GptLiveInputContext>({ scopeKey: "initial" });

  // Active TimerBubble imperative handle.
  // Used only for strict realtime-voice timer commands while normal voice is suspended.
  const timerBubbleRef = useRef<TimerBubbleHandle | null>(null);

  // Active CoachSkillWidget imperative handle.
  // The widget decides which exact voice commands it supports and whether
  // each command is valid for its current state.
  const coachSkillWidgetRef =
    useRef<CoachSkillWidgetHandle | null>(null);

  // Active Coping Skills Buddy imperative handle.
  //
  // Used by both:
  // - GPT-Live voice card selection
  // - legacy realtime voice card selection
  const copingBuddyRef =
    useRef<StepCopingBuddyBubbleHandle | null>(
      null
    );

  // Active Learning Buddy imperative handle.
  //
  // Used by both:
  // - GPT-Live voice card selection
  // - legacy realtime voice card selection
  const learningBuddyRef =
    useRef<StepLearningBuddyBubbleHandle | null>(
      null
    );

  // GPT-Live commentary sender.
  //
  // handleGptLiveDelegation is declared before useGptLiveVoice(),
  // so use a ref to send a verified local UI result back to the
  // active client delegation without creating a declaration cycle.
  const gptLiveSendCommentaryRef =
    useRef<
      | ((
        delegationId: string | null,
        content: string
      ) => boolean)
      | null
    >(null);

  const gptLiveSendSilentUpdateRef =
    useRef<
      | ((
        delegationId: string,
        content: string
      ) => boolean)
      | null
    >(null);

  // GPT-Live exact-speech sender.
  //
  // Used only when spoken output must match authoritative UI text
  // exactly, such as ABC Game "Buddy says".
  const gptLiveSendExactSpeechRef =
    useRef<
      | ((
        delegationId: string,
        content: string
      ) => boolean)
      | null
    >(null);

  // Latest compact application state used to rehydrate
  // a replacement GPT-Live session after reconnect.
  const gptLiveRehydrationContextRef =
    useRef<string>("");

  // GPT-Live timer commands use the existing TimerBubble action path.
  //
  // For terminal actions such as Finished / Finished Early / Can't Finish,
  // remember the originating GPT-Live delegation so handleTimerEvent()
  // can send OCD v4's authoritative next response back to that same
  // Live delegation instead of playing legacy ElevenLabs audio.
  const pendingGptLiveTimerDelegationRef =
    useRef<string | null>(null);

  // GPT-Live ABC Game turns are completed asynchronously inside
  // StepAbcGameBubble.
  //
  // Remember which GPT-Live delegation is waiting for the next
  // authoritative "Buddy says" message.
  const pendingGptLiveAbcGameDelegationRef =
    useRef<string | null>(null);

  // An ABC Game answer was routed directly from
  // GPT-Live transcript fragments, without delegation.
  const pendingGptLiveAbcGameDirectTurnRef =
    useRef(false);

  // Target Game has its own pending GPT-Live response state.
  // Keep separate from ABC Game.
  const pendingGptLiveTargetGameDelegationRef =
    useRef<string | null>(null);

  const pendingGptLiveTargetGameDirectTurnRef =
    useRef(false);

  // Safe nudge reset, available to ABC Game voice handlers.
  const resetActivityRef = useRef<(() => void) | null>(null);

  const resetActivitySafe = useCallback(() => {
    resetActivityRef.current?.();
  }, []);

  const handleAbcGameMessage = useCallback(
    (message: string) => {
      const text = String(message || "").trim();
      if (!text) return;

      const delegationId =
        pendingGptLiveAbcGameDelegationRef.current;

      const isDirectTurn =
        pendingGptLiveAbcGameDirectTurnRef.current;

      // Only speak when an actual GPT-Live game turn
      // is waiting for this authoritative response.
      if (!delegationId && !isDirectTurn) return;

      // Clear both pending states before sending.
      pendingGptLiveAbcGameDelegationRef.current = null;
      pendingGptLiveAbcGameDirectTurnRef.current = false;

      const sent = delegationId
        ? (
          gptLiveSendExactSpeechRef.current?.(
            delegationId,
            text
          ) ?? false
        )
        : (
          gptLiveSendCommentaryRef.current?.(
            null,
            text
          ) ?? false
        );

      if (!sent) {
        console.warn(
          "[GPT-Live][ABC Game] failed to send game response",
          {
            delegationId,
            isDirectTurn,
            message: text,
          }
        );
        return;
      }

      console.info(
        "[GPT-Live][ABC Game] verified response sent",
        {
          delegationId,
          isDirectTurn,
          message: text,
        }
      );
    },
    []
  );


  // --------------------------------------------------
  // GPT-LIVE: AUTHORITATIVE TARGET GAME RESPONSE
  // --------------------------------------------------
  const handleTargetGameMessage = useCallback(
    (message: string) => {
      const text = String(message || "").trim();
      if (!text) return;

      const delegationId =
        pendingGptLiveTargetGameDelegationRef.current;

      const isDirectTurn =
        pendingGptLiveTargetGameDirectTurnRef.current;

      // Do not speak unrelated typed or button-triggered
      // updates through a GPT-Live game turn.
      if (!delegationId && !isDirectTurn) return;

      // Clear before sending to prevent duplicate speech.
      pendingGptLiveTargetGameDelegationRef.current = null;
      pendingGptLiveTargetGameDirectTurnRef.current = false;

      const sent = delegationId
        ? (
          gptLiveSendExactSpeechRef.current?.(
            delegationId,
            text
          ) ?? false
        )
        : (
          gptLiveSendCommentaryRef.current?.(
            null,
            text
          ) ?? false
        );

      if (!sent) {
        console.warn(
          "[GPT-Live][Target Game] failed to send response",
          { delegationId, isDirectTurn, message: text }
        );
        return;
      }

      console.info(
        "[GPT-Live][Target Game] verified response sent",
        { delegationId, isDirectTurn, message: text }
      );
    },
    []
  );


  const handleUndelegatedGptLiveAbcTurn =
    useCallback(
      (transcript: string): boolean => {
        const text =
          stripLeadingGptLiveAudioTags(transcript || "");
        if (!text) return false;

        const localUiText =
          normalizeLocalCopingUiCommand(text) ?? text;

        // Butterfly Breath gets first opportunity
        // to consume a direct spoken UI command.
        const handledByHtmlActivity =
          copingBuddyRef.current
            ?.executeActiveHtmlActivityVoiceCommand(
              localUiText
            ) === true;

        if (handledByHtmlActivity) {
          resetActivitySafe();

          console.info(
            "[GPT-Live][Butterfly Breath] direct voice control",
            { transcript: text }
          );

          return true;
        }


        // --------------------------------------------------
        // COACH SKILLS: UNDELEGATED VOICE CONTROLS
        // --------------------------------------------------
        // Handles Box Breathing and Hills and Valleys
        // even when GPT-Live does not create a delegation.
        const handledByCoachSkill =
          coachSkillWidgetRef.current
            ?.executeVoiceCommand(localUiText) === true ||
          copingBuddyRef.current
            ?.executeActiveCoachSkillVoiceCommand(
              localUiText
            ) === true;

        if (handledByCoachSkill) {
          resetActivitySafe();

          console.info(
            "[GPT-Live][Coach Skill] direct voice control",
            {
              transcript: text,
              normalized: localUiText,
            }
          );

          // Coach Skill owns its prerecorded narration.
          // No game-response pending flag is needed.
          return true;
        }



        // --------------------------------------------------
        // TARGET GAME: UNDELEGATED SPOKEN TURN
        // --------------------------------------------------
        // Set the pending flag BEFORE calling the child.
        // Hint, Quit, and other UI actions may reply
        // synchronously; questions reply after n8n completes.
        pendingGptLiveTargetGameDirectTurnRef.current =
          true;

        const handledByTargetGame =
          copingBuddyRef.current
            ?.executeActiveTargetGameVoiceCommand(localUiText) ===
          true;

        if (handledByTargetGame) {
          resetActivitySafe();

          console.info(
            "[GPT-Live][Target Game] direct voice turn",
            { transcript: text }
          );

          return true;
        }

        pendingGptLiveTargetGameDirectTurnRef.current =
          false;


        // Mark the turn before invoking the game,
        // because its backend response is asynchronous.
        pendingGptLiveAbcGameDirectTurnRef.current = true;

        const handled =
          copingBuddyRef.current
            ?.executeActiveActivityVoiceCommand(
              localUiText
            ) === true;

        if (!handled) {
          pendingGptLiveAbcGameDirectTurnRef.current =
            false;
          return false;
        }

        resetActivitySafe();

        console.info(
          "[GPT-Live][ABC Game] direct voice turn",
          { transcript: text }
        );

        return true;
      },
      [resetActivitySafe]
    );


  // ✅ Suspend realtime voice processing (keep mic on, ignore transcripts)
  const suspendRealtimeVoiceRef = useRef(false);

  // 🔁 Stable ref so both realtime voice and GPT-Live delegation
  // can trigger the existing conversation / n8n path safely.
  const handleSendMessageRef =
    useRef<
      ((text: string, arg?: any) => void | Promise<void>) | undefined
    >(undefined);

  const flushVoiceFinal = useCallback(() => {
    // 🛑 Never flush voice while timer is running
    if (suspendRealtimeVoiceRef.current) {
      voiceFinalBufferRef.current = "";
      return;
    }

    const text = voiceFinalBufferRef.current.trim();
    voiceFinalBufferRef.current = "";
    if (!text) return;

    handleSendMessageRef.current?.(text, {
      dictationOrigin: true,
      fromRealtimeVoice: true,
    });
  }, []);

  const enqueueVoiceFinal = useCallback((finalText: string) => {
    const t = (finalText || "").trim();
    if (!t) return;

    // append with a space if buffer already has content
    voiceFinalBufferRef.current =
      (voiceFinalBufferRef.current ? voiceFinalBufferRef.current + " " : "") + t;

    // reset timer (trailing debounce)
    if (voiceSendTimerRef.current) {
      window.clearTimeout(voiceSendTimerRef.current);
    }
    voiceSendTimerRef.current = window.setTimeout(() => {
      voiceSendTimerRef.current = null;
      flushVoiceFinal();
    }, 900); // tweak 700–1200ms
  }, [flushVoiceFinal]);

  type TimerVoiceCommand =
    | "pause"
    | "resume"
    | "finished"
    | "finishedEarly"
    | "cantFinish";

  const parseTimerVoiceCommand = (raw: string): TimerVoiceCommand | null => {
    const s = (raw || "")
      .trim()
      .toLowerCase()

      // GPT-Live may include nonverbal transcript annotations.
      // They are not part of the user's timer command.
      .replace(
        /\[\s*(?:inhale|exhale|breath|breathing|sigh|pause)\s*\]/gi,
        " "
      )

      .replace(/[’]/g, "'")
      .replace(/[^\w\s']/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    if (!s) return null;

    // Keep this intentionally strict.
    // Avoid broad commands like "stop", "done", or "I'm done" because they can false-trigger.
    if (s === "pause" || s === "paused") return "pause";
    if (s === "resume" || s === "resumed") return "resume";
    if (s === "finish" || s === "finished") return "finished";

    if (
      s === "finish early" ||
      s === "finished early"
    ) {
      return "finishedEarly";
    }

    if (
      s === "can't finish" ||
      s === "cant finish" ||
      s === "can t finish" ||
      s === "cannot finish" ||
      s === "can not finish" ||
      s === "can't finished" ||
      s === "cant finished" ||
      s === "can t finished"
    ) {
      return "cantFinish";
    }

    return null;
  };

  const maybeHandleTimerVoiceCommand = useCallback(
    (raw: string): boolean => {
      // Only allow these commands while a TimerBubble is visibly active.
      // If the voice lock is due to loading/init instead of a timer, ignore as before.
      if (!hasVisibleTimerRef.current) return false;

      const command = parseTimerVoiceCommand(raw);
      if (!command) return false;

      // temporarily add logging to debug
      console.log("[TimerVoice] matched command", {
        raw,
        command,
        hasTimerRef: Boolean(timerBubbleRef.current),
      });

      const timer = timerBubbleRef.current;
      if (!timer) return false;

      resetActivitySafe();

      debugLog("[Realtime Voice][Timer command]", {
        raw,
        command,
      });

      if (command === "pause") {
        timer.pause();
        return true;
      }

      if (command === "resume") {
        timer.resume();
        return true;
      }

      if (command === "finishedEarly") {
        timer.finishEarly();
        return true;
      }
      if (command === "finished") {
        timer.finish();
        return true;
      }

      if (command === "cantFinish") {
        timer.cantFinish();
        return true;
      }

      return false;
    },
    [resetActivitySafe]
  );

  const handleGptLiveDelegation = useCallback(
    async ({
      delegationId,
      transcript,
      offsetMs,
    }: GptLiveClientDelegation) => {
      const text =
        stripLeadingGptLiveAudioTags(transcript || "");

      const localUiText =
        normalizeLocalCopingUiCommand(text) ?? text;

      const selectionText =
        stripLocalSelectionLeadIn(text);

      if (!text) {
        console.warn(
          "[GPT-Live] delegation received without transcript",
          {
            delegationId,
            offsetMs,
          }
        );
        return;
      }

      // --------------------------------------------------
      // GPT-LIVE: ACTIVE TIMER UI ACTION
      // --------------------------------------------------
      //
      // Timer commands must control the actual visible TimerBubble,
      // exactly like legacy realtime voice.
      //
      // This prevents GPT-Live from telling n8n "Finished" while the
      // local React timer continues ticking.
      //
      // Strict commands only:
      //   Pause
      //   Resume
      //   Finished
      //   Finished Early
      //   Can't Finish
      //
      // Anything else continues through the normal GPT-Live flow.
      if (hasVisibleTimerRef.current) {
        const timerCommand =
          parseTimerVoiceCommand(text);

        if (timerCommand) {
          const isTerminalTimerCommand =
            timerCommand === "finished" ||
            timerCommand === "finishedEarly" ||
            timerCommand === "cantFinish";

          // Terminal commands cause TimerBubble -> handleTimerEvent -> n8n.
          // Remember which GPT-Live delegation is waiting for that result.
          if (isTerminalTimerCommand) {
            pendingGptLiveTimerDelegationRef.current =
              delegationId;
          }

          console.info(
            "[GPT-Live][Timer] local UI action",
            {
              delegationId,
              transcript: text,
              timerCommand,
            }
          );

          const handled =
            maybeHandleTimerVoiceCommand(text);

          if (handled) {
            // Pause / Resume do not go through the terminal timer baton.
            // Return a short local result directly to GPT-Live.
            if (!isTerminalTimerCommand) {
              const sendCommentary =
                gptLiveSendCommentaryRef.current;

              if (sendCommentary) {
                const commentary =
                  timerCommand === "pause"
                    ? "Timer paused."
                    : "Timer resumed.";

                sendCommentary(
                  delegationId,
                  commentary
                );
              }
            }

            // IMPORTANT:
            // Do not send this transcript through normal handleSendMessage().
            return;
          }

          // Defensive cleanup if the visible timer ref was unexpectedly unavailable.
          if (
            pendingGptLiveTimerDelegationRef.current ===
            delegationId
          ) {
            pendingGptLiveTimerDelegationRef.current =
              null;
          }
        }
      }


      // --------------------------------------------------
      // GPT-LIVE: BUTTERFLY BREATH VOICE CONTROLS
      // --------------------------------------------------
      const handledByHtmlActivity =
        copingBuddyRef.current
          ?.executeActiveHtmlActivityVoiceCommand(localUiText) === true;

      if (handledByHtmlActivity) {
        resetActivitySafe();

        console.info(
          "[GPT-Live][Butterfly Breath] local UI action",
          {
            delegationId,
            transcript: text,
          }
        );

        // The HTML activity controls its own animation.
        // Do not ask GPT-Live to narrate the action.
        const sent =
          gptLiveSendSilentUpdateRef.current?.(
            delegationId,
            "Butterfly Breath handled the user's control. The activity owns its animation. Do not narrate the action."
          );

        if (!sent) {
          console.warn(
            "[GPT-Live][Butterfly Breath] silent update unavailable",
            { delegationId }
          );
        }

        return;
      }


      // --------------------------------------------------
      // GPT-LIVE: ACTIVE COACH SKILL CONTROLS
      // Handles BOTH homework and nested Coping Buddy.
      // --------------------------------------------------
      const handledByCoachSkill =
        coachSkillWidgetRef.current
          ?.executeVoiceCommand(localUiText) === true ||
        copingBuddyRef.current
          ?.executeActiveCoachSkillVoiceCommand(localUiText) === true;

      if (handledByCoachSkill) {
        resetActivitySafe();

        console.info(
          "[GPT-Live][Coach Skill] local UI action",
          {
            delegationId,
            transcript: text,
          }
        );

        // The widget owns its prerecorded narration.
        // Do not ask GPT-Live to speak "Okay" or count.
        const sent =
          gptLiveSendSilentUpdateRef.current?.(
            delegationId,
            `The breathing widget handled the spoken control "${text}". The widget provides its own prerecorded audio.`
          );

        if (!sent) {
          console.warn(
            "[GPT-Live][Coach Skill] silent update unavailable",
            { delegationId }
          );
        }

        return;
      }


      // --------------------------------------------------
      // GPT-LIVE: ACTIVE TARGET GAME
      // --------------------------------------------------
      // Hold the delegation while Target Game processes
      // the question and obtains its verified response.
      pendingGptLiveTargetGameDelegationRef.current =
        delegationId;

      const handledByTargetGame =
        copingBuddyRef.current
          ?.executeActiveTargetGameVoiceCommand(localUiText) ===
        true;

      if (handledByTargetGame) {
        resetActivitySafe();

        console.info(
          "[GPT-Live][Target Game] delegated voice turn",
          { delegationId, transcript: text }
        );

        // Do not invent a game answer here.
        // StepTargetGameBubble will call onGameMessage
        // after the game produces its response.
        return;
      }

      // Target Game didn't own the utterance.
      if (
        pendingGptLiveTargetGameDelegationRef.current ===
        delegationId
      ) {
        pendingGptLiveTargetGameDelegationRef.current =
          null;
      }


      // --------------------------------------------------
      // GPT-LIVE: ACTIVE COPING BUDDY CHILD ACTIVITY
      // --------------------------------------------------
      //
      // If ABC Game is already open, its backend owns the
      // authoritative game response.
      //
      // Remember the GPT-Live delegation BEFORE invoking the
      // child because the ABC Game response arrives asynchronously.
      pendingGptLiveAbcGameDelegationRef.current =
        delegationId;

      const handledByActiveCopingActivity =
        copingBuddyRef.current
          ?.executeActiveActivityVoiceCommand(
            localUiText
          ) === true;

      if (handledByActiveCopingActivity) {
        resetActivitySafe();

        console.info(
          "[GPT-Live][Coping Buddy child activity]",
          {
            delegationId,
            transcript: text,
          }
        );

        // IMPORTANT:
        //
        // Do NOT send "Got it."
        // Do NOT let GPT-Live invent the next game instruction.
        //
        // StepAbcGameBubble will call onGameMessage() after
        // its ABC Game backend response arrives.
        //
        // handleAbcGameMessage() will then send the exact
        // authoritative "Buddy says" text back to this
        // GPT-Live delegation.
        return;
      }

      // ABC Game did not own this utterance.
      //
      // Remove the pending delegation so some unrelated future
      // game update cannot accidentally speak into this turn.
      if (
        pendingGptLiveAbcGameDelegationRef.current ===
        delegationId
      ) {
        pendingGptLiveAbcGameDelegationRef.current =
          null;
      }

      // --------------------------------------------------
      // GPT-LIVE: ACTIVE COPING BUDDY UI ACTION
      // --------------------------------------------------
      //
      // Give the currently active Coping Skills Buddy the first
      // opportunity to handle an exact spoken card selection.
      //
      // This is intentionally GPT-Live-only for the first rollout.
      // The legacy realtime voice path is not changed.
      //
      // Examples:
      //   "Five senses grounding"
      //   "Let's do box breathing"
      //   "Choose ABC game"
      //
      // If no current card matches exactly, continue through the
      // normal GPT-Live → n8n path below.
      const selectedCopingSkill =
        copingBuddyRef.current
          ?.executeVoiceCommand(selectionText) ??
        null;

      if (selectedCopingSkill) {
        resetActivitySafe();

        console.info(
          "[GPT-Live][Coping Buddy] local UI action",
          {
            delegationId,
            transcript: text,
            selectedCopingSkill,
          }
        );

        const isAbcGame =
          selectedCopingSkill
            .trim()
            .toLowerCase() ===
          "abc game";

        if (isAbcGame) {
          // ABC Game will mount and load/start its authoritative
          // state asynchronously.
          //
          // Hold this delegation until StepAbcGameBubble emits
          // the actual initial "Buddy says" message.
          pendingGptLiveAbcGameDelegationRef.current =
            delegationId;

          // Do NOT say "Opening ABC Game."
          //
          // The next spoken response should instead be the
          // exact initial game instruction, for example:
          //
          // "Great — you start. Name an animal that starts with A."
          return;
        }

        // The UI action has already completed locally.
        //
        // Return a verified result to the same GPT-Live client
        // delegation so Live does not remain waiting for backend work.
        const sendCommentary =
          gptLiveSendCommentaryRef.current;

        if (sendCommentary) {
          const selectedName =
            selectedCopingSkill.trim().toLowerCase();

          const openingMessage =
            selectedName === "target game"
              ? "Opening Target Game. Ask a yes-or-no question or make a guess when the game appears."
              : `Opening ${selectedCopingSkill}.`;

          const commentarySent =
            sendCommentary(
              delegationId,
              openingMessage
            );

          if (!commentarySent) {
            console.warn(
              "[GPT-Live][Coping Buddy] failed to return local UI result",
              {
                delegationId,
                selectedCopingSkill,
              }
            );
          }
        } else {
          console.warn(
            "[GPT-Live][Coping Buddy] commentary sender unavailable",
            {
              delegationId,
              selectedCopingSkill,
            }
          );
        }

        // IMPORTANT:
        // Do not create a chat bubble and do not call n8n.
        // This is equivalent to clicking the visible Coping Buddy card.
        return;
      }

      // --------------------------------------------------
      // GPT-LIVE: ACTIVE LEARNING BUDDY UI ACTION
      // --------------------------------------------------
      //
      // Give the currently active Learning Buddy the opportunity
      // to handle an exact spoken card selection.
      //
      // Examples:
      //   "Understanding SUDS"
      //   "Let's do What Is ERP"
      //   "Choose Values Sort"
      //
      // If no current activity matches, continue through the
      // normal GPT-Live → n8n path below.
      const selectedLearningActivity =
        learningBuddyRef.current
          ?.executeVoiceCommand(selectionText) ??
        null;

      if (selectedLearningActivity) {
        resetActivitySafe();

        console.info(
          "[GPT-Live][Learning Buddy] local UI action",
          {
            delegationId,
            transcript: text,
            selectedLearningActivity,
          }
        );

        const sendCommentary =
          gptLiveSendCommentaryRef.current;

        if (sendCommentary) {
          const commentarySent =
            sendCommentary(
              delegationId,
              `Opening ${selectedLearningActivity}.`
            );

          if (!commentarySent) {
            console.warn(
              "[GPT-Live][Learning Buddy] failed to return local UI result",
              {
                delegationId,
                selectedLearningActivity,
              }
            );
          }
        } else {
          console.warn(
            "[GPT-Live][Learning Buddy] commentary sender unavailable",
            {
              delegationId,
              selectedLearningActivity,
            }
          );
        }

        // Local UI action only.
        // Do not add a chat bubble and do not call n8n.
        return;
      }

      const send = handleSendMessageRef.current;

      if (!send) {
        console.warn(
          "[GPT-Live] handleSendMessage is not ready",
          {
            delegationId,
            transcript: text,
          }
        );
        return;
      }

      console.info(
        "[GPT-Live] forwarding delegation to OCD v4",
        {
          delegationId,
          transcript: text,
          offsetMs,
        }
      );

      await Promise.resolve(
        send(text, {
          dictationOrigin: true,

          // Preserve the existing spoken-input normalization
          // for homework choices, SUDS values, etc.
          fromRealtimeVoice: true,

          // Distinguish GPT-Live from the legacy realtime voice path.
          fromGptLive: true,

          // Used to send the n8n result back to the correct
          // GPT-Live delegation.
          gptLiveDelegationId: delegationId,
        })
      );
    },
    []
  );

  const gptLiveVoice = useGptLiveVoice({
    enabled: ENABLE_GPT_LIVE_1,
    getConversationId: () => sessionId,
    onClientDelegation: handleGptLiveDelegation,
    getInputContext: () => gptLiveInputContextRef.current,
    onUndelegatedHomeworkChoice: (selection) => {
      const send = handleSendMessageRef.current;
      if (!send) return false;
      // No server delegation exists. Use the existing UI-originated reply
      // path so the result is returned to Live with a null delegation ID.
      void Promise.resolve(send(selection, {
        dictationOrigin: true,
        fromRealtimeVoice: true,
        fromUiAction: true,
      })).catch((error) => console.error("[GPT-Live] homework choice failed", error));
      return true;
    },

    onUndelegatedGameTurn:
      handleUndelegatedGptLiveAbcTurn,

    getRehydrationContext: () =>
      gptLiveRehydrationContextRef.current,
  });

  useEffect(() => {
    gptLiveSendCommentaryRef.current =
      gptLiveVoice.sendCommentary;

    return () => {
      gptLiveSendCommentaryRef.current =
        null;
    };
  }, [gptLiveVoice.sendCommentary]);

  // NEW: Connect the silent GPT-Live update function.
  useEffect(() => {
    gptLiveSendSilentUpdateRef.current =
      gptLiveVoice.sendSilentUpdate;

    return () => {
      gptLiveSendSilentUpdateRef.current = null;
    };
  }, [gptLiveVoice.sendSilentUpdate]);

  useEffect(() => {
    gptLiveSendExactSpeechRef.current =
      gptLiveVoice.sendExactSpeech;

    return () => {
      gptLiveSendExactSpeechRef.current =
        null;
    };
  }, [gptLiveVoice.sendExactSpeech]);

  // 🔊 TEMP: Realtime Voice test (STT only)
  const realtimeVoice = useRealtimeVoice({
    enabled: ENABLE_REALTIME_VOICE && !ENABLE_GPT_LIVE_1,
    onPartialTranscript: (text) => {
      debugLog("🎙 PARTIAL:", text.slice(0, 80));
    },
    onFinalTranscript: (text) => {
      const t = (text || "").trim();
      if (!t) return;

      // ✅ Highest priority: while a timer is visible,
      // allow only strict timer commands.
      // Timer commands remain separate from Coach Skill commands.
      if (hasVisibleTimerRef.current) {
        if (voiceSendTimerRef.current) {
          window.clearTimeout(
            voiceSendTimerRef.current
          );
          voiceSendTimerRef.current = null;
        }

        voiceFinalBufferRef.current = "";

        maybeHandleTimerVoiceCommand(t);

        return;
      }

      // --------------------------------------------------
      // REALTIME VOICE: ACTIVE COACH SKILL UI ACTION
      // --------------------------------------------------
      //
      // Give the active Coach Skill the first opportunity
      // to handle an exact local command such as
      // Start, Pause, Resume, or Reset.
      //
      // If it returns false, continue below.
      const handledByCoachSkill =
        coachSkillWidgetRef.current
          ?.executeVoiceCommand(t) === true;

      if (handledByCoachSkill) {
        if (voiceSendTimerRef.current) {
          window.clearTimeout(
            voiceSendTimerRef.current
          );
          voiceSendTimerRef.current = null;
        }

        voiceFinalBufferRef.current = "";
        resetActivitySafe();

        debugLog(
          "[Realtime Voice][Coach Skill command]",
          {
            transcript: t,
          }
        );

        return;
      }

      // --------------------------------------------------
      // REALTIME VOICE: ACTIVE COPING BUDDY CHILD ACTIVITY
      // --------------------------------------------------
      //
      // Give an already-open embedded Coping Buddy activity
      // the first opportunity to consume the transcript.
      //
      // Currently this is used by ABC Game.
      //
      // Examples while ABC Game is active:
      //   "Anteater"
      //   "Hint"
      //   "Pass"
      //   "Start over"
      //   "Quit"
      //
      // If ABC Game has ended, it returns false and the
      // transcript continues through the normal routing below.
      const handledByActiveCopingActivity =
        copingBuddyRef.current
          ?.executeActiveActivityVoiceCommand(t) ===
        true;

      if (handledByActiveCopingActivity) {
        if (voiceSendTimerRef.current) {
          window.clearTimeout(
            voiceSendTimerRef.current
          );
          voiceSendTimerRef.current = null;
        }

        // Prevent this same utterance from later flushing
        // into the normal chat / n8n path.
        voiceFinalBufferRef.current = "";

        resetActivitySafe();

        debugLog(
          "[Realtime Voice][Coping Buddy child activity]",
          {
            transcript: t,
          }
        );

        // The active embedded activity already consumed
        // this utterance.
        //
        // Do not create a user chat bubble and
        // do not call the normal n8n chat workflow.
        return;
      }

      // --------------------------------------------------
      // REALTIME VOICE: ACTIVE COPING BUDDY UI ACTION
      // --------------------------------------------------
      //
      // Give the currently active Coping Skills Buddy the
      // same opportunity to handle a spoken card selection
      // that GPT-Live already has.
      //
      // Examples:
      //   "Box Breathing"
      //   "Let's try Hills and Valleys"
      //   "Five Senses Grounding"
      //   "ABC Game"
      //
      // If no Coping Buddy card matches, continue below.
      const selectedCopingSkillRealtime =
        copingBuddyRef.current
          ?.executeVoiceCommand(t) ??
        null;

      if (selectedCopingSkillRealtime) {
        if (voiceSendTimerRef.current) {
          window.clearTimeout(
            voiceSendTimerRef.current
          );
          voiceSendTimerRef.current = null;
        }

        voiceFinalBufferRef.current = "";
        resetActivitySafe();

        debugLog(
          "[Realtime Voice][Coping Buddy] local UI action",
          {
            transcript: t,
            selectedCopingSkill:
              selectedCopingSkillRealtime,
          }
        );

        // Equivalent to clicking the visible
        // Coping Skills Buddy card.
        //
        // Do not add a user chat bubble and
        // do not call n8n.
        return;
      }

      // --------------------------------------------------
      // REALTIME VOICE: ACTIVE LEARNING BUDDY UI ACTION
      // --------------------------------------------------
      //
      // Give the currently active Learning Buddy the same
      // opportunity to handle a spoken card selection
      // that GPT-Live already has.
      //
      // Examples:
      //   "Understanding SUDS"
      //   "What is ERP"
      //   "Values Card Sort"
      //   "Value Card Sort"
      //   "Anxiety Body Map"
      //   "Anxiety Buddy Map"
      //
      // If no Learning Buddy card matches, continue
      // through the normal realtime voice → n8n path.
      const selectedLearningActivityRealtime =
        learningBuddyRef.current
          ?.executeVoiceCommand(t) ??
        null;

      if (selectedLearningActivityRealtime) {
        if (voiceSendTimerRef.current) {
          window.clearTimeout(
            voiceSendTimerRef.current
          );
          voiceSendTimerRef.current = null;
        }

        voiceFinalBufferRef.current = "";
        resetActivitySafe();

        debugLog(
          "[Realtime Voice][Learning Buddy] local UI action",
          {
            transcript: t,
            selectedLearningActivity:
              selectedLearningActivityRealtime,
          }
        );

        // Equivalent to clicking the visible
        // Learning Buddy card.
        //
        // Do not add a user chat bubble and
        // do not call n8n.
        return;
      }

      // ✅ suspended => ignore normal transcripts
      // but keep the microphone active.
      if (suspendRealtimeVoiceRef.current) {
        return;
      }

      enqueueVoiceFinal(t);
    },
  });

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<ChatInputHandle | null>(null);

  // 🧠 Baton ref: was the *last assistant step* a Q&A step?
  // Must be a ref (NOT state) to avoid race conditions
  const lastAssistantIsQAStepRef = useRef<boolean>(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);

  // 👇 On first mount, make sure the inner chat scroller starts at the top
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const reset = () => {
      el.scrollTop = 0;
    };

    // run a couple of times in case layout shifts during init
    reset();
    const t1 = window.setTimeout(reset, 50);
    const t2 = window.setTimeout(reset, 250);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, []);

  useEffect(() => {
    if (isAtBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isLoading, isAtBottom]);

  useEffect(() => {
    if (!pendingRewardScrollRef.current) return;
    pendingRewardScrollRef.current = false;

    // iOS sometimes needs 2 frames after layout/image insertion
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
      });
    });
  }, [messages]);

  useEffect(() => {
    const primary = localStorage.getItem(AUDIO_TOGGLE_KEY);
    const alt = localStorage.getItem(ALT_AUDIO_TOGGLE_KEY);
    if (primary == null && alt == null) {
      localStorage.setItem(AUDIO_TOGGLE_KEY, "false");
      debugLog("[audioPlayback] defaulted → false");
    }
  }, []);

  // 👇 Ensure the window itself starts scrolled to the very top (iOS Safari)
  useEffect(() => {
    if (typeof window !== "undefined" && "scrollTo" in window) {
      window.scrollTo(0, 0);
    }
  }, []);


  const [accessCode, setAccessCode] = useState<string | null>(
    localStorage.getItem("accessCode")
  );

  const lastAccessCodeRef = useRef<string>(
    (localStorage.getItem("accessCode") ?? "").trim().toUpperCase()
  );

  useEffect(() => {
    const nextCode = (accessCode ?? "").trim().toUpperCase();
    const prevCode = lastAccessCodeRef.current;

    // Compare against the previous React-observed access code, not localStorage.
    // Other components may already have written the new code to localStorage.
    if (prevCode && nextCode && prevCode !== nextCode) {
      localStorage.removeItem(AIRTABLE_PATIENT_RECORD_ID_KEY);
      localStorage.removeItem(AIRTABLE_PATIENT_RECORD_ID_ACCESS_CODE_KEY);

      console.info("[INIT] cleared Airtable record id because access code changed:", {
        prevCode,
        nextCode,
      });
    }

    if (nextCode) {
      localStorage.setItem("accessCode", nextCode);
    } else {
      localStorage.removeItem("accessCode");
      localStorage.removeItem(AIRTABLE_PATIENT_RECORD_ID_KEY);
      localStorage.removeItem(AIRTABLE_PATIENT_RECORD_ID_ACCESS_CODE_KEY);
    }

    lastAccessCodeRef.current = nextCode;
  }, [accessCode]);

  // 🎙 Voice selection (single source of truth)
  const [voiceKey, setVoiceKey] = useState<VoiceKey>(() => {
    return (
      (localStorage.getItem("voiceKey") as VoiceKey) || DEFAULT_VOICE_KEY
    );
  });

  // 🆕 live voice update from ProfileSettingsModal (Option A)
  const handleVoiceKeyChange = useCallback((k: VoiceKey) => {
    setVoiceKey(k);
    localStorage.setItem("voiceKey", k);
  }, []);

  // 🔄 Keep voiceKey in sync if changed in ProfileSettingsModal
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "voiceKey" && e.newValue) {
        setVoiceKey(e.newValue as VoiceKey);
        debugLog("[voiceKey] updated via storage:", e.newValue);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // ✅ Resolved voice profile (static + optional Base64 clips)
  const voiceProfile = useMemo(() => {
    const resolved = VOICES[voiceKey] ?? VOICES[DEFAULT_VOICE_KEY];

    const base64Set = VOICE_BASE64[voiceKey];

    debugLog("🎙 [voiceProfile resolved]", {
      voiceKey,
      label: resolved.label,
      hasBase64: Boolean(base64Set),
      elevenLabsVoiceId: resolved.elevenLabsVoiceId,
    });

    return {
      ...resolved,
      base64: base64Set, // { greeting?, retrieveHomework?, ... }
    };
  }, [voiceKey]);

  // ✅ Always-latest voiceProfile for callbacks that have empty deps (e.g., N8N_RUN_INIT)
  const voiceProfileRef = useRef(voiceProfile);
  useEffect(() => {
    voiceProfileRef.current = voiceProfile;
  }, [voiceProfile]);

  const [sessionId, setSessionId] = useState<string>(() => {
    const existing = sessionStorage.getItem("sessionId");
    if (existing) return existing;
    const id =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem("sessionId", id);
    return id;
  });

  // ✅ Session guard: prevents late async responses from a prior session
  // from mutating the current chat after "New chat".
  const activeSessionIdRef = useRef<string>(sessionId);
  useEffect(() => {
    activeSessionIdRef.current = sessionId;
  }, [sessionId]);

  const isActiveSession = (sidAtSend: string) => sidAtSend === activeSessionIdRef.current;

  const initTokenRef = useRef(0);
  const [showInitOverlay, setShowInitOverlay] = useState(false);
  const initHideTimerRef = useRef<number | null>(null);

  const didInitKeyRef = useRef<string | null>(null); // StrictMode guard: once per (sessionId, accessCode)
  const INIT_DEDUP_SEC = 4;

  const MIN_INIT_OVERLAY_MS = 900;

  const startOverlay = () => setShowInitOverlay(true);
  const finishOverlayAfterMin = (startedAt: number) => {
    const elapsed = performance.now() - startedAt;
    const delay = Math.max(0, MIN_INIT_OVERLAY_MS - elapsed);
    initHideTimerRef.current = window.setTimeout(() => {
      setShowInitOverlay(false);
      initHideTimerRef.current = null;
    }, delay);
  };

  const N8N_RUN_INIT = useCallback(async (code: string, sid: string) => {
    if (!code || !N8N_INIT_URL) return;

    const token = ++initTokenRef.current;
    const t0 = performance.now();
    startOverlay();

    let initOk = false;

    try {
      const normalizedCode = code.trim().toUpperCase();

      const cachedAirtablePatientRecordId =
        localStorage.getItem(AIRTABLE_PATIENT_RECORD_ID_KEY)?.trim() || "";

      const cachedRecordIdAccessCode =
        (localStorage.getItem(AIRTABLE_PATIENT_RECORD_ID_ACCESS_CODE_KEY) ?? "")
          .trim()
          .toUpperCase();

      const safeCachedAirtablePatientRecordId =
        cachedRecordIdAccessCode === normalizedCode
          ? cachedAirtablePatientRecordId
          : "";

      if (cachedAirtablePatientRecordId && !safeCachedAirtablePatientRecordId) {
        console.warn("[INIT] ignoring stale Airtable record id:", {
          normalizedCode,
          cachedRecordIdAccessCode,
          cachedAirtablePatientRecordId,
        });
      }

      const initPayload = {
        accessCode: normalizedCode,
        sessionId: sid,
        ...(safeCachedAirtablePatientRecordId
          ? { airtablePatientRecordId: safeCachedAirtablePatientRecordId }
          : {}),
      };

      console.info("[INIT] outgoing payload:", {
        initPayload,
        cachedAirtablePatientRecordId,
        cachedRecordIdAccessCode,
        safeCachedAirtablePatientRecordId,
        storedAccessCode: localStorage.getItem("accessCode"),
        storedRecordId: localStorage.getItem(AIRTABLE_PATIENT_RECORD_ID_KEY),
      });

      const res = await fetch(N8N_INIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(initPayload),
      });

      let data: any = await res.json().catch(() => ({}));

      // normalize array response
      // normalize array response
      if (Array.isArray(data) && data.length > 0) data = data[0];

      console.info("[INIT] parsed normalized response:", {
        keys: data && typeof data === "object" ? Object.keys(data) : null,
        status: data?.status,
        session_id: data?.session_id,
        access_code: data?.access_code,
        airtablePatientRecordId: data?.airtablePatientRecordId,
      });

      if (token !== initTokenRef.current) return;

      const returnedDb = data?.db ?? data?.meta?.db;
      if (returnedDb === "airtable" || returnedDb === "supabase") {
        if (!DATABASE_ROUTING_CONFIGURED || returnedDb === RUNTIME_DB) {
          setRuntimeDb(returnedDb);
        } else {
          console.error("[INIT] database response does not match selected endpoint", {
            selectedDb: RUNTIME_DB,
            returnedDb,
          });
        }
      }

      const errMsg =
        (typeof data?.debugBody?.message === "string" ? data.debugBody.message : null) ||
        (data?.status === "error" && typeof data?.message === "string" ? data.message : null);

      const isInvalidCode =
        data?.debugBody?.status === "error" ||
        data?.status === "error" ||
        /invalid access code/i.test(errMsg ?? "");

      if (isInvalidCode) {
        localStorage.removeItem(AIRTABLE_PATIENT_RECORD_ID_KEY);
        localStorage.removeItem(AIRTABLE_PATIENT_RECORD_ID_ACCESS_CODE_KEY);

        setAccessCodeValid(false);
        setInitError(errMsg ?? "❌ Invalid access code. Please double-check or set a valid one.");

        setMessages((prev) => [
          ...prev,
          {
            id: String(Date.now()) + "-init-error",
            text: errMsg ?? "❌ Invalid access code. Please double-check or set a valid one.",
            sender: "system",
            timestamp: new Date(),
          },
        ]);

        return;
      }

      // init succeeded
      setAccessCodeValid(true);
      setInitError(null);
      initOk = true;

      const returnedAirtablePatientRecordId =
        typeof data?.airtablePatientRecordId === "string"
          ? data.airtablePatientRecordId.trim()
          : "";

      if (returnedAirtablePatientRecordId) {
        localStorage.setItem(
          AIRTABLE_PATIENT_RECORD_ID_KEY,
          returnedAirtablePatientRecordId
        );

        localStorage.setItem(
          AIRTABLE_PATIENT_RECORD_ID_ACCESS_CODE_KEY,
          code.trim().toUpperCase()
        );

        console.info("[INIT] saved Airtable patient record id:", {
          returnedAirtablePatientRecordId,
          storedRecordId: localStorage.getItem(AIRTABLE_PATIENT_RECORD_ID_KEY),
        });
      } else {
        console.warn("[INIT] no Airtable patient record id found to save:", {
          rawValue: data?.airtablePatientRecordId,
          keys: data && typeof data === "object" ? Object.keys(data) : null,
        });
      }

      const meta = data?.meta ?? {};

      // Reset rank when Init returns a blank value so a previous patient's
      // badge cannot remain visible in the current session.
      const level = meta.badgeLevel ?? data?.badgeLevel ?? null;

      setBadgeLevel(
        typeof level === "string" && level.trim()
          ? (level.trim() as any)
          : null
      );

      // Support both the current meta-based response and older root-level
      // efa-chat Init responses.
      const collection =
        meta.badgeCollection ?? data?.badgeCollection;

      setBadgeCollection(
        Array.isArray(collection)
          ? collection.filter(
            (item: unknown): item is string =>
              typeof item === "string" &&
              item.trim().length > 0
          )
          : []
      );

      // Points and coins display independently from badgeLevel.
      setPointsEarned(
        toNumber(meta.pointsEarned ?? data?.pointsEarned)
      );

      setCoins(
        toNumber(meta.coins ?? data?.coins)
      );
      setUnlockedItems(Array.isArray(meta.unlockedItems) ? meta.unlockedItems : []);
      setEquippedItems(Array.isArray(meta.equippedItems) ? meta.equippedItems : []);
      setSelfInitiatedPermitted(Boolean(meta.selfInitiatedPermitted));

      const derivedUserId =
        data?.session_id || data?.therapist?.id || data?.therapist?.email || String(data?.id ?? "unknown-user");
      setProfileUserId(derivedUserId);

      setNickname(typeof data?.nickname === "string" ? data.nickname : null);

      const returnedHomeworkSummary =
        data?.homeworkSummary ??
        data?.summary_hw_data ??
        null;

      setHomeworkSummary(
        returnedHomeworkSummary &&
          typeof returnedHomeworkSummary === "object"
          ? returnedHomeworkSummary
          : null
      );

      setPracticeCalendarHomework(
        Array.isArray(data?.practiceCalendarHomework)
          ? data.practiceCalendarHomework
          : []
      );

      if (typeof data?.avatarId === "string" && data.avatarId.trim() !== "") {
        setAvatarId(data.avatarId);
      }
    } catch (e) {
      console.error("[INIT] exception:", e);
    } finally {
      finishOverlayAfterMin(t0);
      setInitComplete(true);

      // only greet if init actually succeeded
      if (initOk && getOutboundType() === "audio" && !ENABLE_GPT_LIVE_1) {
        const greetingB64 = voiceProfileRef.current.base64?.greeting;
        if (greetingB64) {
          try {
            await playTts(greetingB64, "audio/mpeg", undefined, SYSTEM_TTS_VOLUME);
          } catch (e) {
            console.warn("⚠️ Greeting audio failed:", e);
          }
        }
      }
    }
  }, []);

  useEffect(() => {
    const code = (accessCode ?? "").trim();
    if (!sessionId || !code) return;

    const key = `${sessionId}:${code}`;

    // 1) StrictMode/dev replay guard (in-memory only)
    if (didInitKeyRef.current === key) return;

    // 2) Short-TTL session guard — prevents instant dupes, allows reload later
    const now = Date.now();
    const raw = sessionStorage.getItem("didInit");
    let prev: { key?: string; ts?: number } = {};
    try { prev = raw ? JSON.parse(raw) : {}; } catch { }

    // if same key and within TTL, skip
    if (prev.key === key && typeof prev.ts === "number" && now - prev.ts < INIT_DEDUP_SEC * 1000) {
      didInitKeyRef.current = key;
      return;
    }

    // record this attempt with fresh timestamp
    didInitKeyRef.current = key;
    sessionStorage.setItem("didInit", JSON.stringify({ key, ts: now }));

    N8N_RUN_INIT(code, sessionId);
  }, [sessionId, accessCode, N8N_RUN_INIT]);

  const getOutboundType = (): "text" | "audio" => {
    const primary = localStorage.getItem(AUDIO_TOGGLE_KEY);
    const alt = localStorage.getItem(ALT_AUDIO_TOGGLE_KEY);
    const on = primary ?? alt;
    return on === "true" ? "audio" : "text";
  };

  const cleanOutput = (s: string) =>
    s.replace(/\\"/g, '"').replace(/\\n/g, "\n");

  const extractReplyText = (input: any, seen = new Set<any>()): string => {
    if (input == null) return "";
    if (typeof input === "string") {
      try {
        return extractReplyText(JSON.parse(input), seen);
      } catch {
        return cleanOutput(input);
      }
    }
    if (typeof input !== "object" || seen.has(input)) return "";
    seen.add(input);
    const keys = ["text", "reply", "message", "content", "output", "result"];
    for (const k of keys)
      if (k in (input as any)) {
        const ans = extractReplyText((input as any)[k], seen);
        if (ans) return cleanOutput(ans);
      }
    for (const v of Object.values(input)) {
      const ans = extractReplyText(v, seen);
      if (ans) return cleanOutput(ans);
    }
    return "";
  };

  type TimerPayload = {
    duration: number;           // ✅ required (matches Message type)
    label?: string;
    key?: string;

    ended?: boolean;
    remaining?: number;
    elapsed?: number;

    // keep extra backend fields
    [k: string]: any;
  };

  const extractTimer = (input: any, seen = new Set<any>()): TimerPayload | null => {
    if (input == null) return null;
    if (typeof input === "string") {
      try {
        return extractTimer(JSON.parse(input), seen);
      } catch {
        return null;
      }
    }
    if (typeof input !== "object" || seen.has(input)) return null;
    seen.add(input);

    if ("timer" in (input as any)) {
      const timer = (input as any).timer;
      if (timer && typeof timer === "object") {
        const durSec = timer.durationSeconds ?? timer.duration ?? 120;
        return {
          ...timer,
          // normalize and keep both names available
          durationSeconds: durSec,
          duration: timer.duration ?? durSec,
        };
      }
      return null;
    }

    for (const v of Object.values(input)) {
      const ans = extractTimer(v, seen);
      if (ans) return ans;
    }
    return null;
  };

  const normalizeResp = (data: any) =>
    Array.isArray(data) && data.length === 1 ? data[0] : data;

  const pickField = (input: any, key: string, seen = new Set<any>()): any => {
    if (input == null || seen.has(input)) return undefined;

    // Recurse into arrays (covers webhook responses that are arrays)
    if (Array.isArray(input)) {
      for (const el of input) {
        const found = pickField(el, key, seen);
        if (found !== undefined) return found;
      }
      return undefined;
    }

    if (typeof input !== "object") return undefined;
    seen.add(input);

    if (key in (input as any)) return (input as any)[key];

    for (const v of Object.values(input)) {
      const found = pickField(v, key, seen);
      if (found !== undefined) return found;
    }
    return undefined;
  };


  const pickStr = (input: any, key: string) => {
    const v = pickField(input, key);
    return typeof v === "string" ? v : v != null ? String(v) : undefined;
  };

  // Extract boolean values like homeworkMode from nested replies
  const pickBool = (input: any, key: string) => {
    const v = pickField(input, key);
    if (typeof v === "boolean") return v;
    if (typeof v === "string") {
      const s = v.trim().toLowerCase();
      return s === "true" || s === "yes" || s === "1";
    }
    return undefined;
  };

  const extractSudsControl = (
    input: any
  ): Message["sudsControl"] | null => {
    const raw = pickField(input, "sudsControl");

    if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
      return null;
    }

    const styleRaw = String(raw.style ?? "")
      .trim()
      .toLowerCase();

    const style =
      styleRaw === "slider"
        ? "slider"
        : styleRaw === "gauge" || styleRaw === "dial"
          ? "gauge"
          : styleRaw === "emoji"
            ? "emoji"
            : "buttons";

    const stageRaw = String(raw.stage ?? "")
      .trim()
      .toLowerCase();

    const stage =
      stageRaw === "after"
        ? "after"
        : stageRaw === "coping_loop"
          ? "coping_loop"
          : "before";

    const awaitKeyRaw = String(raw.awaitKey ?? "").trim();

    const awaitKey =
      awaitKeyRaw === "SUDS2"
        ? "SUDS2"
        : awaitKeyRaw === "coping_loop_suds"
          ? "coping_loop_suds"
          : "SUDS1";

    const min = Number(raw.min);
    const max = Number(raw.max);
    const step = Number(raw.step);

    return {
      style,
      min: Number.isFinite(min) ? min : 0,
      max: Number.isFinite(max) ? max : 10,
      step: Number.isFinite(step) && step > 0 ? step : 1,
      stage,
      awaitKey,
    };
  };

  const extractAttachments = (input: any): ChatAttachment[] => {
    const attachments = pickField(input, "attachments");

    if (!Array.isArray(attachments)) return [];

    return attachments
      .filter(
        (attachment: any) =>
          attachment &&
          typeof attachment === "object" &&
          typeof attachment.downloadUrl === "string" &&
          attachment.downloadUrl.trim() !== ""
      )
      .map((attachment: any) => ({
        type:
          typeof attachment.type === "string" &&
            attachment.type.trim() !== ""
            ? attachment.type.trim()
            : "file",

        label:
          typeof attachment.label === "string" &&
            attachment.label.trim() !== ""
            ? attachment.label.trim()
            : "Download file",

        filename:
          typeof attachment.filename === "string" &&
            attachment.filename.trim() !== ""
            ? attachment.filename.trim()
            : "Download",

        mimeType:
          typeof attachment.mimeType === "string" &&
            attachment.mimeType.trim() !== ""
            ? attachment.mimeType.trim()
            : "application/octet-stream",

        size:
          typeof attachment.size === "number" &&
            Number.isFinite(attachment.size)
            ? attachment.size
            : null,

        downloadUrl: attachment.downloadUrl.trim(),

        helperText:
          typeof attachment.helperText === "string" &&
            attachment.helperText.trim() !== ""
            ? attachment.helperText.trim()
            : undefined,
      }));
  };

  const normalizeArtifactType = (type: any): string | null => {
    const t = String(type || "").trim();

    if (t === "expectancyTracker") return "expectancy_tracker";
    if (t === "expectancy_tracker") return "expectancy_tracker";

    if (t === "abcGame") return "abc_game";
    if (t === "abc_game") return "abc_game";

    if (t === "targetGame") return "target_game";
    if (t === "target_game") return "target_game";

    if (t === "copingBuddy") return "coping_buddy";
    if (t === "coping_buddy") return "coping_buddy";

    if (t === "learningBuddy") return "learning_buddy";
    if (t === "learning_buddy") return "learning_buddy";

    if (t === "imaginaryHandwashing") return "imaginary_handwashing";
    if (t === "imaginary_handwashing") return "imaginary_handwashing";

    return t || null;
  };

  const enrichArtifactWithAudioContext = (
    artifact: Message["artifact"] | null | undefined,
    source: any
  ): Message["artifact"] | null | undefined => {
    if (!artifact || typeof artifact !== "object") return artifact;

    const sourceType =
      pickStr(source, "type") ??
      pickStr(source, "responseType") ??
      pickStr(source, "outputType");

    const responseType = sourceType === "audio" ? "audio" : "text";

    const voiceId =
      pickStr(source, "voiceId") ??
      pickStr(source, "voice_id") ??
      null;

    const inputMode =
      pickStr(source, "inputMode") ??
      null;

    const dictationOriginValue = pickField(source, "dictationOrigin");
    const dictationOrigin =
      typeof dictationOriginValue === "boolean"
        ? dictationOriginValue
        : String(dictationOriginValue ?? "").toLowerCase() === "true";

    const existingContext =
      (artifact as any).context && typeof (artifact as any).context === "object"
        ? (artifact as any).context
        : {};

    const existingConfig =
      (artifact as any).config && typeof (artifact as any).config === "object"
        ? (artifact as any).config
        : {};

    return {
      ...(artifact as any),
      context: {
        ...existingContext,
        responseType: existingContext.responseType ?? responseType,
        voiceId: existingContext.voiceId ?? voiceId,
        inputMode: existingContext.inputMode ?? inputMode,
        dictationOrigin: existingContext.dictationOrigin ?? dictationOrigin,
      },
      config: {
        ...existingConfig,
        voiceId: existingConfig.voiceId ?? voiceId,
      },
    } as Message["artifact"];
  };

  const toImaginaryHandwashingArtifact = (a: any): Message["artifact"] => {
    return {
      id: a.id || a.key || `imaginary-handwashing-${Date.now()}`,
      stepId: a.stepId || "0",
      label: a.label || a.title || "20-Second Handwashing Challenge",
      hint:
        a.hint ||
        "Drag the hands over contaminated objects, then practice a brief handwashing routine.",
      type: "imaginary_handwashing",
      title: a.title || "20-Second Handwashing Challenge",
      key: a.key || `imaginary-handwashing-${Date.now()}`,
      context: a.context || {},
      mode: a.mode || "homework_step",
      config: a.config || {},
      api: a.api || {},
    } as Message["artifact"];
  };

  const extractArtifact = (
    input: any,
    seen = new Set<any>()
  ): Message["artifact"] | null => {
    if (input == null) return null;

    if (typeof input === "string") {
      try {
        return extractArtifact(JSON.parse(input), seen);
      } catch {
        return null;
      }
    }

    if (typeof input !== "object" || seen.has(input)) return null;
    seen.add(input);

    const obj = input as any;

    // 1) canonical artifact
    if (obj.artifact && typeof obj.artifact === "object") {
      const a = obj.artifact;

      const artifactType = normalizeArtifactType(a.type);

      if (artifactType === "expectancy_tracker") {
        return {
          id: a.key || `expectancy-tracker-${Date.now()}`,
          stepId: "0",
          label: a.title || "Expectancy Tracker",
          hint: "Review what you predicted, what actually happened, and what you learned.",
          type: "expectancy_tracker",
          title: a.title || "Expectancy Tracker",
          key: a.key || `expectancy-tracker-${Date.now()}`,
          context: a.context || {},
          mode: a.mode || "list",
          allowCreate: a.allowCreate ?? true,
          allowEdit: a.allowEdit ?? true,
          allowDelete: a.allowDelete ?? true,
          api: a.api || {},
        } as Message["artifact"];
      }

      if (artifactType === "abc_game") {
        return {
          id: a.key || `abc-game-${Date.now()}`,
          stepId: "0",
          label: a.title || "ABC Game",
          hint: "Practice naming foods or animals in alphabetical order.",
          type: "abc_game",
          title: a.title || "ABC Game",
          key: a.key || `abc-game-${Date.now()}`,
          context: a.context || {},
          mode: a.mode || "practice",
          allowRestart: a.allowRestart ?? true,
          allowQuit: a.allowQuit ?? true,
          api: a.api || {},
        } as Message["artifact"];
      }
      if (artifactType === "target_game") {
        return {
          id: a.id || a.key || `target-game-${Date.now()}`,
          stepId: a.stepId || "0",
          label: a.label || a.title || "Target Game",
          hint: a.hint || "Ask yes or no questions to narrow down the mystery item.",
          type: "target_game",
          title: a.title || "Target Game",
          key: a.key || `target-game-${Date.now()}`,
          context: a.context || {},
          mode: a.mode || "practice",
          config: a.config || {},
        } as Message["artifact"];
      }

      if (artifactType === "coping_buddy") {
        return {
          id: a.id || a.key || `coping-buddy-${Date.now()}`,
          stepId: a.stepId || "0",
          label: a.label || a.title || "Coping Buddy",
          hint: a.hint || "Choose a coping skill to practice.",
          type: "coping_buddy",
          title: a.title || "Coping Buddy",
          key: a.key || `coping-buddy-${Date.now()}`,
          context: a.context || {},
          mode: a.mode || "practice",
          skills: Array.isArray(a.skills) ? a.skills : [],
        } as Message["artifact"];
      }

      if (artifactType === "learning_buddy") {
        return {
          id: a.id || a.key || `learning-buddy-${Date.now()}`,
          stepId: a.stepId || "0",
          label: a.label || a.title || "Learning Buddy",
          hint: a.hint || "Choose a learning activity.",
          type: "learning_buddy",
          title: a.title || "Learning Buddy",
          key: a.key || `learning-buddy-${Date.now()}`,
          context: a.context || {},
          mode: a.mode || "practice",
          activities: Array.isArray(a.activities) ? a.activities : [],
        } as Message["artifact"];
      }

      if (artifactType === "imaginary_handwashing") {
        return toImaginaryHandwashingArtifact({
          ...a,
          type: artifactType,
        });
      }

      return a as Message["artifact"];
    }

    // 2) future-friendly widget envelope
    if (obj.widget && typeof obj.widget === "object") {
      const w = obj.widget;
      const widgetType = normalizeArtifactType(w.type);

      if (widgetType === "expectancy_tracker") {
        return {
          id: w.key || `expectancy-tracker-${Date.now()}`,
          stepId: "0",
          label: w.title || "Expectancy Tracker",
          hint: "Review what you predicted, what actually happened, and what you learned.",
          type: "expectancy_tracker",
          title: w.title || "Expectancy Tracker",
          key: w.key || `expectancy-tracker-${Date.now()}`,
          context: w.context || {},
          mode: w.mode || "list",
          allowCreate: w.allowCreate ?? true,
          allowEdit: w.allowEdit ?? true,
          allowDelete: w.allowDelete ?? true,
          api: w.api || {},
        } as Message["artifact"];
      }

      if (widgetType === "abc_game") {
        return {
          id: w.key || `abc-game-${Date.now()}`,
          stepId: "0",
          label: w.title || "ABC Game",
          hint: "Practice naming foods or animals in alphabetical order.",
          type: "abc_game",
          title: w.title || "ABC Game",
          key: w.key || `abc-game-${Date.now()}`,
          context: w.context || {},
          mode: w.mode || "practice",
          allowRestart: w.allowRestart ?? true,
          allowQuit: w.allowQuit ?? true,
          api: w.api || {},
        } as Message["artifact"];
      }

      if (widgetType === "target_game") {
        return {
          id: w.id || w.key || `target-game-${Date.now()}`,
          stepId: w.stepId || "0",
          label: w.label || w.title || "Target Game",
          hint: w.hint || "Ask yes or no questions to narrow down the mystery item.",
          type: "target_game",
          title: w.title || "Target Game",
          key: w.key || `target-game-${Date.now()}`,
          context: w.context || {},
          mode: w.mode || "practice",
          config: w.config || {},
        } as Message["artifact"];
      }

      if (widgetType === "coping_buddy") {
        return {
          id: w.id || w.key || `coping-buddy-${Date.now()}`,
          stepId: w.stepId || "0",
          label: w.label || w.title || "Coping Buddy",
          hint: w.hint || "Choose a coping skill to practice.",
          type: "coping_buddy",
          title: w.title || "Coping Buddy",
          key: w.key || `coping-buddy-${Date.now()}`,
          context: w.context || {},
          mode: w.mode || "practice",
          skills: Array.isArray(w.skills) ? w.skills : [],
        } as Message["artifact"];
      }

      if (widgetType === "learning_buddy") {
        return {
          id: w.id || w.key || `learning-buddy-${Date.now()}`,
          stepId: w.stepId || "0",
          label: w.label || w.title || "Learning Buddy",
          hint: w.hint || "Choose a learning activity.",
          type: "learning_buddy",
          title: w.title || "Learning Buddy",
          key: w.key || `learning-buddy-${Date.now()}`,
          context: w.context || {},
          mode: w.mode || "practice",
          activities: Array.isArray(w.activities) ? w.activities : [],
        } as Message["artifact"];
      }

      if (widgetType === "imaginary_handwashing") {
        return toImaginaryHandwashingArtifact({
          ...w,
          type: widgetType,
        });
      }
      return w as Message["artifact"];
    }

    // 3) legacy rollout field
    if (obj.expectancyTracker && typeof obj.expectancyTracker === "object") {
      const et = obj.expectancyTracker;

      return {
        id: et.key || `expectancy-tracker-${Date.now()}`,
        stepId: "0",
        label: et.title || "Expectancy Tracker",
        hint: "Review what you predicted, what actually happened, and what you learned.",
        type: "expectancy_tracker",
        title: et.title || "Expectancy Tracker",
        key: et.key || `expectancy-tracker-${Date.now()}`,
        context: et.context || {},
        mode: et.mode || "list",
        allowCreate: et.allowCreate ?? true,
        allowEdit: et.allowEdit ?? true,
        allowDelete: et.allowDelete ?? true,
        api: et.api || {},
      } as Message["artifact"];
    }

    if (obj.abcGame && typeof obj.abcGame === "object") {
      const ag = obj.abcGame;

      return {
        id: ag.key || `abc-game-${Date.now()}`,
        stepId: "0",
        label: ag.title || "ABC Game",
        hint: "Practice naming foods or animals in alphabetical order.",
        type: "abc_game",
        title: ag.title || "ABC Game",
        key: ag.key || `abc-game-${Date.now()}`,
        context: ag.context || {},
        mode: ag.mode || "practice",
        allowRestart: ag.allowRestart ?? true,
        allowQuit: ag.allowQuit ?? true,
        api: ag.api || {},
      } as Message["artifact"];
    }

    if (obj.targetGame && typeof obj.targetGame === "object") {
      const tg = obj.targetGame;

      return {
        id: tg.id || tg.key || `target-game-${Date.now()}`,
        stepId: tg.stepId || "0",
        label: tg.label || tg.title || "Target Game",
        hint: tg.hint || "Ask yes or no questions to narrow down the mystery item.",
        type: "target_game",
        title: tg.title || "Target Game",
        key: tg.key || `target-game-${Date.now()}`,
        context: tg.context || {},
        mode: tg.mode || "practice",
        config: tg.config || {},
      } as Message["artifact"];
    }

    // Add this:
    if (obj.imaginaryHandwashing && typeof obj.imaginaryHandwashing === "object") {
      return toImaginaryHandwashingArtifact(obj.imaginaryHandwashing);
    }

    for (const v of Object.values(obj)) {
      const ans = extractArtifact(v, seen);
      if (ans) return ans;
    }

    return null;
  };

  const extractSelectedHomework = (input: any): SelectedHomework | null => {
    const obj = pickField(input, "selectedHomework") ?? pickField(input, "selected_homework");
    if (obj && typeof obj === "object" && "recordId" in obj) {
      return obj as SelectedHomework;
    }
    return null;
  };

  const audioElRef = useRef<HTMLAudioElement | null>(null);

  const lastAudioSigRef = useRef<string | null>(null);
  const lastAudioAtRef = useRef<number>(0);
  const revokeTimerRef = useRef<number | null>(null);

  // 🧮 Prevent double-applying rewards (StrictMode / replay guard)
  const lastRewardSigRef = useRef<string | null>(null);

  const DUP_AUDIO_GUARD_MS = 1200; // only suppress identical clips within this window


  useEffect(() => {
    audioElRef.current = new Audio();
  }, []);

  function resolveAudioUrl(u: string): string {
    try {
      const url = new URL(u, window.location.href);

      // ✅ upgrade http -> https when app is https (prevents mixed-content block)
      if (window.location.protocol === "https:" && url.protocol === "http:") {
        url.protocol = "https:";
      }

      return url.toString();
    } catch {
      return u; // if it's a relative path or weird string, leave it alone
    }
  }

  const playTts = async (
    base64?: string,
    mime?: string,
    audioUrl?: string,
    volume: number = 1.0 // 👈 Defaults to 100% if you forget to pass it
  ) => {
    debugLog("🔊 playTts called", {
      hasBase64: Boolean(base64),
      mime,
      hasAudioUrl: Boolean(audioUrl),
      base64Preview: base64 ? `${base64.slice(0, 6)}…${base64.slice(-6)}` : null,
    });

    let src: string | null = null;
    let blobUrl: string | null = null;

    // Signature to avoid duplicate rapid replays
    const sig =
      audioUrl
        ? `url:${audioUrl}`
        : base64 && mime
          ? `${mime}:${base64.length}:${base64.slice(0, 8)}:${base64.slice(-8)}`
          : null;

    // ✅ 1) If we have an audioUrl (mp3), resolve + fetch as blob (more reliable than direct URL)
    if (audioUrl) {
      const resolved = resolveAudioUrl(audioUrl);

      try {
        debugLog("🎧 [playTts] audioUrl (resolved):", resolved);

        const r = await fetch(resolved, { cache: "no-store" });
        if (!r.ok) throw new Error(`audioUrl fetch failed: ${r.status} ${r.statusText}`);

        const blob = await r.blob();
        blobUrl = URL.createObjectURL(blob);
        src = blobUrl;

        debugLog("🎧 [playTts] audioUrl -> blob ok:", {
          type: blob.type,
          size: blob.size,
          blobUrl,
        });
      } catch (e) {
        console.warn("⚠️ [playTts] audioUrl blob fetch failed; fallback to direct URL", e);
        src = resolved; // fallback: let <audio> try direct URL
      }
    }

    // ✅ 2) Else if base64, use your existing base64->blob logic
    if (!src && !audioUrl && base64 && mime) {
      try {
        const bin = atob(base64);
        const len = bin.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
        const blob = new Blob([bytes], { type: mime });
        blobUrl = URL.createObjectURL(blob);
        src = blobUrl;
      } catch (e) {
        console.warn("[playTts] base64→blob failed; fallback to data: URL", e);
        src = `data:${mime};base64,${base64}`;
      }
    }

    if (!src || !sig) return;

    try {
      await ensureAudioContext();

      if (!audioElRef.current) audioElRef.current = new Audio();
      const el = audioElRef.current;

      // Stop any current playback before swapping
      try { el.pause(); } catch { }
      el.srcObject = null;
      el.currentTime = 0;
      el.autoplay = false;
      el.volume = volume; // 👈 This applies your 0.6 or 1.0 scale
      el.preload = "auto";

      // Duplicate guard (time-windowed)
      const now = Date.now();
      if (sig === lastAudioSigRef.current && now - lastAudioAtRef.current < DUP_AUDIO_GUARD_MS) {
        debugLog("🔁 Skipping near-duplicate audio playback");
        if (blobUrl) URL.revokeObjectURL(blobUrl);
        return;
      }
      lastAudioSigRef.current = sig;
      lastAudioAtRef.current = now;

      // Set source
      debugLog("🎧 Setting audio src", {
        src,
        isRelative: src.startsWith("/"),
        isHttp: src.startsWith("http"),
        isBlob: src.startsWith("blob:"),
      });
      el.src = src;

      // Wait until buffered enough (prevents “blip”)
      await new Promise<void>((resolve, reject) => {
        let settled = false;
        const onCanPlay = () => { if (!settled) { settled = true; cleanup(); resolve(); } };
        const onErr = (e: any) => { if (!settled) { settled = true; cleanup(); reject(e); } };
        const onMeta = () => {
          debugLog("🎧 metadata:", { duration: el.duration, readyState: el.readyState });
        };
        const cleanup = () => {
          el.removeEventListener("canplaythrough", onCanPlay);
          el.removeEventListener("loadedmetadata", onMeta);
          el.removeEventListener("error", onErr);
        };
        el.addEventListener("canplaythrough", onCanPlay, { once: true });
        el.addEventListener("loadedmetadata", onMeta, { once: true });
        el.addEventListener("error", onErr, { once: true });
        el.load();
        // safety: resolve after 3s even if canplaythrough didn’t fire
        setTimeout(() => { if (!settled) onCanPlay(); }, 3000);
      });

      await ensureAudioContext();

      // Cleanup blob URL when audio ends or errors
      const cleanupBlob = () => {
        if (blobUrl) {
          if (revokeTimerRef.current) {
            window.clearTimeout(revokeTimerRef.current);
            revokeTimerRef.current = null;
          }
          // ...
          revokeTimerRef.current = window.setTimeout(() => {
            URL.revokeObjectURL(blobUrl!);
            revokeTimerRef.current = null;
          }, 500);
        }
        el.removeEventListener("ended", cleanupBlob);
        el.removeEventListener("error", cleanupBlob);
      };
      el.addEventListener("ended", cleanupBlob);
      el.addEventListener("error", cleanupBlob);

      await el.play().catch((err) => {
        console.error("❌ audio.play() failed", err);
        const retry = () => {
          debugLog("🔁 retrying audio.play()");
          el.play().catch(() => { });
          document.removeEventListener("touchstart", retry);
          document.removeEventListener("click", retry);
        };
        document.addEventListener("touchstart", retry, { once: true });
        document.addEventListener("click", retry, { once: true });
      });

    } catch (err) {
      console.error("[playTts] failed:", err);
      // best-effort revoke on failure
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    }
  };

  // ------------------------------------------------------
  // GPT-Live connection / disconnection sounds
  //
  // Play a short confirmation cue when GPT-Live:
  // - connects:    false -> true
  // - disconnects: true  -> false
  // ------------------------------------------------------
  const previousGptLiveConnectedRef =
    useRef(false);

  useEffect(() => {
    const wasConnected =
      previousGptLiveConnectedRef.current;

    const isConnected =
      gptLiveVoice.isConnected;

    previousGptLiveConnectedRef.current =
      isConnected;

    // Connected
    if (!wasConnected && isConnected) {
      void playTts(
        undefined,
        "audio/mpeg",
        "/assets/sounds/live-connect.mp3",
        0.5
      );

      return;
    }

    // Disconnected
    if (wasConnected && !isConnected) {
      void playTts(
        undefined,
        "audio/mpeg",
        "/assets/sounds/live-disconnect.mp3",
        0.5
      );
    }
  }, [gptLiveVoice.isConnected]);

  const handleWidgetAudioResponse = useCallback(
    async (
      audioBase64?: string | null,
      mimeType?: string | null,
      audioUrl?: string | null
    ) => {
      if (getOutboundType() !== "audio") return;

      const hasAudio =
        Boolean(audioBase64 && audioBase64.trim()) ||
        Boolean(audioUrl && audioUrl.trim());

      if (!hasAudio) return;

      await playTts(
        audioBase64 ?? undefined,
        mimeType ?? "audio/mpeg",
        audioUrl ?? undefined,
        1.0
      );
    },
    [voiceKey]
  );

  const [sudsEnforce, setSudsEnforce] = useState(false);

  // For coping-loop SUDS only:
  // keep the configured SUDS input hidden until the user says they are done practicing.
  // Store the assistant message id so a later coping loop starts fresh.
  const [
    copingPracticeFinishedMessageId,
    setCopingPracticeFinishedMessageId,
  ] = useState<string | null>(null);

  const handleCoachSkillActivityEvent = useCallback(
    async (event: CoachSkillActivityEvent) => {
      const code = accessCode || "";
      if (!code || !sessionId) {
        console.warn("[ActivityEvent] missing sessionId/accessCode", {
          sessionId,
          accessCode: code,
          event,
        });
        return;
      }

      const baseChatInput = `${event.eventType} ${event.activityTitle} from ${event.widgetLabel}`;

      const thoughtText =
        typeof event.metadata?.thoughtText === "string"
          ? event.metadata.thoughtText.trim()
          : "";

      const chatInput =
        event.activityKey === "leaves_on_a_stream" &&
          event.eventType === "submitted" &&
          thoughtText
          ? `${baseChatInput}: ${thoughtText}`
          : baseChatInput;

      try {
        debugLog("[ActivityEvent] sending to OCD v4", {
          chatInput,
          event,
        });

        const res = await fetch(requireWebhookUrl(), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            origin: "activity_event",
            type: "text",
            inputMode: "widget_event",
            deviceType,
            chatInput,
            accessCode: code,
            sessionId,
            activityEvent: event,
          }),
        });

        if (!res.ok) {
          const text = await res.text().catch(() => "");
          throw new Error(`Activity event capture failed: ${res.status} ${text}`);
        }
      } catch (err) {
        console.error("[ActivityEvent] failed to capture coach skill event", err);
      }
    },
    [accessCode, sessionId, deviceType]
  );

  // ─────────────────────────────────────────────────────────────
  // UI ACTION / REALTIME VOICE BEHAVIOR
  //
  // Control / Action                     Behavior
  // -------------------------------------------------------------
  // Finished                             Send immediately
  // Homework number                     Send immediately
  // Coping skill number                 Send immediately
  // ALTERED choices                     Send immediately
  //   - No changes
  //   - Didn't finish
  //   - Completed with modification
  // SUDS Number Buttons                 Send immediately
  //
  // SUDS Slider                         Select/adjust, then Submit
  // SUDS Gauge / Dial                   Select/adjust, then Submit
  // SUDS Emoji                          Select, then Submit
  //
  // Done practicing                     Local UI action; do not send
  // Coach Skill controls                Local widget action
  // Timer controls                      Dedicated timer-event path
  // Welcome/dashboard suggestions       Prefill composer
  // Normal typed text                   User presses Send
  //
  // REALTIME VOICE:
  // - Spoken final answers send immediately.
  // - UI action sends must NOT stop realtime voice.
  // - Before a UI action send, clear any queued realtime transcript
  //   so a partial spoken phrase cannot flush after the button click.
  // - Slider / Gauge / Emoji retain their explicit Submit step.
  // ───────────────────────────────────────────────────────────── 
  // user → webhook
  const sendMessageToWebhook = async (
    message: string,
    type: "text" | "audio",
    dictationOrigin: boolean,
    fromRealtimeVoice: boolean,   // ✅ ADD
    awaitKey?: string,
    signals?: Record<string, any>
  ) => {
    const payload = {
      origin: "user",
      type,
      inputMode: fromRealtimeVoice ? "realtime_voice" : "text",
      deviceType,
      [REQUEST_TEXT_KEY]: message,
      accessCode: accessCode || "",
      sessionId,
      dictationOrigin,
      voiceId: voiceProfile.elevenLabsVoiceId,
      ...(awaitKey ? { awaitKey } : {}),
      ...(signals ? { signals } : {}),
    };
    const res = await fetch(requireWebhookUrl(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const raw = await res.text();

    debugLog("🔎 RAW response length:", raw?.length);

    let data: any = {};
    try {
      data = normalizeResp(raw ? JSON.parse(raw) : {});

      debugLog("🧪 parsed data keys:", Object.keys(data || {}));

    } catch { }

    // ✅ Keep the full response (array or object) for awaitKey/signals discovery
    // ✅ But pick a “replyCarrier” for assistant-text/audio/timer extraction
    const replyCarrier =
      Array.isArray(data)
        ? (data.find(
          (x) =>
            x &&
            typeof x === "object" &&
            ("audioBase64" in x || "audioUrl" in x || "message" in x || "reply" in x || "text" in x)
        ) ?? data[0])
        : data;

    const replyAttachments = extractAttachments(replyCarrier);
    const fallbackAttachments = extractAttachments(data);

    const response = {
      // Prefer carrier for display text
      text:
        extractReplyText(replyCarrier) ||
        extractReplyText(data) ||
        "Message received!",

      // Keep temporary download URLs only in the frontend message state
      attachments:
        replyAttachments.length > 0
          ? replyAttachments
          : fallbackAttachments,

      // Prefer carrier for timer/audio/artifact
      timer: extractTimer(replyCarrier) || extractTimer(data) || undefined,

      sudsControl:
        extractSudsControl(replyCarrier) ??
        extractSudsControl(data) ??
        null,

      audioBase64: pickStr(replyCarrier, "audioBase64") ?? pickStr(data, "audioBase64"),
      mimeType: pickStr(replyCarrier, "mimeType") ?? pickStr(data, "mimeType"),
      audioUrl: pickStr(replyCarrier, "audioUrl") ?? pickStr(data, "audioUrl"),
      confetti: Boolean(pickField(replyCarrier, "confetti") ?? pickField(data, "confetti")),
      artifact:
        enrichArtifactWithAudioContext(
          extractArtifact(replyCarrier) || extractArtifact(data) || undefined,
          data
        ) || undefined,
      selectedHomework: extractSelectedHomework(data) || undefined, // keep full data
      homeworkMode: pickBool(data, "homeworkMode") ?? undefined,
      coachPrompts: Array.isArray(pickField(replyCarrier, "coachPrompts"))
        ? pickField(replyCarrier, "coachPrompts")
        : [],
      coachSkill: pickStr(replyCarrier, "coachSkill") ?? pickStr(data, "coachSkill") ?? undefined,
      pointsEarned: toNumber(pickField(data, "pointsEarned")),
      coinsEarned: toNumber(pickField(data, "coinsEarned")),

      mode:
        pickStr(data, "mode") ||
        pickStr(data, "state.mode") ||
        pickStr(data, "parameters.mode"),

      // 👇 These often come back as “sidecar” items in array responses, so search full data
      awaitKey: pickStr(data, "awaitKey") || undefined,
      signals: {
        awaitKey: pickStr(data, "awaitKey") ?? null,
        _lastListType: pickStr(data, "_lastListType"),
        _copingPickMax: pickField(data, "_copingPickMax"),
        _resolvedCopingList: pickField(data, "_resolvedCopingList"),
        requireLoopSuds: pickBool(data, "requireLoopSuds"),
      },

      intent: pickStr(data, "intent") || undefined,
      isQAStep: pickBool(data, "isQAStep") ?? undefined,
    };

    debugLog("🧪 extracted response snapshot:", {
      hasText: Boolean(response.text),
      hasTimer: Boolean(response.timer),
      hasArtifact: Boolean(response.artifact),
      artifactType: (response.artifact as any)?.type ?? null,
      hasSudsControl: Boolean(response.sudsControl),
      sudsStyle: response.sudsControl?.style ?? null,
      sudsStage: response.sudsControl?.stage ?? null,
      mode: response.mode,
    });

    debugLog("🧪 artifact audio context:", {
      topLevelType: pickStr(data, "type"),
      topLevelVoiceId: pickStr(data, "voiceId"),
      artifactType: (response.artifact as any)?.type ?? null,
      artifactContext: (response.artifact as any)?.context ?? null,
      artifactConfig: (response.artifact as any)?.config ?? null,
    });

    // If artifact is missing, show top-level keys to catch structure changes
    // If artifact is missing, log shape only (debug-safe)
    if (!response.artifact) {
      debugLog("🧪 artifact missing — response shape", {
        isArray: Array.isArray(data),
        type: typeof data,
        keys: typeof data === "object" && data ? Object.keys(data) : null,
      });
    }

    // 🩵 Fallback: use top-level message if extractReplyText() returned nothing
    if ((!response.text || response.text.trim() === "") && data?.message) {
      response.text = data.message;
    }

    //    console.info("[Safety Plan attachment debug]", {
    //      replyAttachments,
    //      fallbackAttachments,
    //      finalAttachments: response.attachments,
    //    });

    return response;
  };

  // ── Context-aware system message detector ─────────────────────────────
  function detectSystemPhase(
    userText: string,
    lastAssistantText?: string
  ): "searchHomework" | "saveHomework" | "saveExposure" | "generic" {
    const u = (userText || "").toLowerCase();
    const a = (lastAssistantText || "").toLowerCase();

    // (1) user asking to see or continue homework
    if (
      /\b(what('?s| is) my homework|show (me )?my homework|assigned homework|homework list|next homework|show next homework|continue homework|let('?s)? do (more )?homework|do (more )?homework|what homework do i have|what homework (do i|have i) (have|got)|what homework am i (supposed )?to do)\b/i.test(u)
    ) {
      return "searchHomework";
    }

    // (2) assistant prompting for altered/final step (all variations)
    if (
      // original patterns
      /\bDid you (?:change(?:\s+or\s+skip)?|skip|alter|make(?: any)? changes?)\b/i.test(a) ||
      /\bexactly as planned\b/i.test(a) ||

      // NEW: Variation #2 — "Were any parts of the steps changed or skipped?"
      /\bwere (?:any )?parts? of the steps? (?:changed|skipped).*?\b/i.test(a) ||

      // NEW: Variation #3 — "Let me know if you changed or skipped any step."
      /\blet me know if you (?:changed|skipped) (?:any )?steps?\b/i.test(a)
    ) {
      return "saveHomework";
    }

    // (3) assistant prompting for coping skills
    if (
      /\bdid you use (any )?coping skill(s)?/i.test(a) ||
      /\bwhat coping skill(s)?/i.test(a) ||
      /\bwhat strateg(y|ies)\b/i.test(a)
    ) {
      return "saveExposure";
    }

    // (4) everything else
    return "generic";
  }

  const handleSendMessage = async (messageText: string, arg?: any) => {

    debugLog("🧠 [Q&A][READ_ENTER]", {
      fromRealtimeVoice: Boolean(arg?.fromRealtimeVoice),
      ts: performance.now(),
    });


    resetActivitySafe(); // reset nudge timer

    // ✅ Identify the input source once.
    //
    // Realtime voice and discrete UI actions are both committed answers,
    // so neither should shut down an already-active realtime voice session.
    const fromRealtimeVoice = arg?.fromRealtimeVoice === true;
    const fromUiAction = arg?.fromUiAction === true;

    const fromGptLive =
      arg?.fromGptLive === true;

    // GPT-Live owns output whenever its voice session is active,
    // regardless of whether input was spoken, clicked, or typed.
    const gptLiveOwnsOutput =
      fromGptLive ||
      (ENABLE_GPT_LIVE_1 && gptLiveVoice.isConnected);

    const gptLiveDelegationId =
      typeof arg?.gptLiveDelegationId === "string"
        ? arg.gptLiveDelegationId.trim()
        : "";

    // ✅ Realtime Voice: "Done practicing" is a local coping UI command.
    //
    // n8n is already waiting for coping_loop_suds, so do NOT send this
    // intermediate phrase to OCD v4. Mirror the local Done practicing button:
    // reveal the configured Number Buttons / Slider / Gauge / Emoji, then let the
    // patient's spoken SUDS number or Emoji category use the normal realtime voice path.
    if (fromRealtimeVoice) {
      const normalizedVoiceCommand = (messageText || "")
        .trim()
        .toLowerCase()
        .replace(/[’]/g, "'")
        .replace(/[^\w\s']/g, " ")
        .replace(/\s+/g, " ")
        .trim();

      const isDonePracticingVoiceCommand =
        /^(?:i(?:'m| am)\s+)?(?:done|finished)\s+practi[cs]ing$/.test(
          normalizedVoiceCommand
        );

      if (
        isDonePracticingVoiceCommand &&
        String(pendingAwaitKey ?? "")
          .trim()
          .toLowerCase() === "coping_loop_suds"
      ) {
        const currentAssistant =
          [...messages]
            .reverse()
            .find((m) => m.sender === "assistant") ?? null;

        const currentArtifactType =
          String(currentAssistant?.artifact?.type ?? "");

        const hasCopingPracticeUi = Boolean(
          currentAssistant &&
          (
            (
              currentAssistant.intent === "COPING_GUIDE" &&
              currentAssistant.mode === "GUIDE" &&
              currentAssistant.coachSkill
            ) ||
            currentArtifactType === "coping_buddy" ||
            currentArtifactType === "abc_game" ||
            currentArtifactType === "target_game" ||
            currentArtifactType === "html_activity"
          )
        );

        const isPendingCopingLoopSuds =
          currentAssistant?.sudsControl?.stage === "coping_loop" &&
          String(currentAssistant?.sudsControl?.awaitKey ?? "")
            .trim()
            .toLowerCase() === "coping_loop_suds";

        if (
          currentAssistant &&
          isPendingCopingLoopSuds &&
          hasCopingPracticeUi &&
          copingPracticeFinishedMessageId !== currentAssistant.id
        ) {
          // Prevent any queued realtime transcript from leaking through.
          if (voiceSendTimerRef.current) {
            window.clearTimeout(voiceSendTimerRef.current);
            voiceSendTimerRef.current = null;
          }

          voiceFinalBufferRef.current = "";

          // Same local transition as clicking Done practicing.
          setCopingPracticeFinishedMessageId(currentAssistant.id);

          resetActivitySafe();

          debugLog("[Realtime Voice][Done practicing]", {
            transcript: messageText,
            assistantMessageId: currentAssistant.id,
            sudsStyle: currentAssistant.sudsControl?.style ?? null,
          });

          // Bring the newly revealed SUDS control into view.
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              messagesEndRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "end",
              });
            });
          });

          // IMPORTANT: local UI command only.
          // Do not add a user bubble and do not call n8n.
          return;
        }
      }
    }

    // Final Coach Skill command check.
    //
    // The realtime transcript may initially arrive before the newly rendered
    // CoachSkillWidget ref has attached. If that happens, the transcript enters
    // the short voice buffer. By the time handleSendMessage runs, the widget ref
    // should be available, so check again before adding a user bubble or calling n8n.
    //
    // This also intentionally allows exact typed commands while the active
    // breathing widget is open.
    const handledByActiveCoachSkill =
      coachSkillWidgetRef.current?.executeVoiceCommand(messageText) === true;

    if (handledByActiveCoachSkill) {
      if (voiceSendTimerRef.current) {
        window.clearTimeout(voiceSendTimerRef.current);
        voiceSendTimerRef.current = null;
      }

      voiceFinalBufferRef.current = "";
      resetActivitySafe();

      debugLog("[Coach Skill command][send fallback]", {
        transcript: messageText,
        fromRealtimeVoice,
      });

      return;
    }

    // 🛑 A true manual/typed composer send ends realtime voice.
    //
    // Realtime speech and committed UI actions do NOT stop the mic.
    // UI actions use sendUiAction(), which also clears any queued
    // realtime transcript before sending.
    if (
      ENABLE_REALTIME_VOICE &&
      realtimeVoice.isActive &&
      !fromRealtimeVoice &&
      !fromUiAction
    ) {
      realtimeVoice.stop();
    }

    // ✅ Voice-only single-word digit normalization (avoid typed regressions)
    // (use `fromRealtimeVoice` below — do NOT redeclare it)

    // We can safely inspect the last assistant prompt here (no ordering issues)
    const lastAssistant = [...messages].reverse().find((m) => m.sender === "assistant");
    const awaitingSuds =
      // if your backend uses awaitKey for SUDS, this catches it
      (pendingAwaitKey ? /suds/i.test(pendingAwaitKey) : false) ||
      // fallback: detect SUDS prompt text
      isSudsPrompt(lastAssistant?.text || "");

    // Start from the raw user text
    let textForNormalization = messageText;

    // Only for realtime voice + only when awaiting SUDS.
    if (fromRealtimeVoice && awaitingSuds) {
      const isEmojiSuds =
        String(lastAssistant?.sudsControl?.style ?? "")
          .trim()
          .toLowerCase() === "emoji";

      // Emoji SUDS supports spoken category names:
      // low → 2, moderate → 5, high → 8.
      //
      // Spoken numeric SUDS still works as a fallback.
      if (isEmojiSuds) {
        const maybeEmojiValue =
          normalizeVoiceEmojiSudsCategory(messageText);

        if (maybeEmojiValue !== null) {
          textForNormalization = maybeEmojiValue;
        } else {
          const maybeDigit =
            normalizeVoiceSingleWordDigit(messageText);

          if (maybeDigit !== null) {
            textForNormalization = maybeDigit;
          }
        }
      } else {
        // Existing Slider / Gauge / Number Buttons behavior.
        const maybeDigit =
          normalizeVoiceSingleWordDigit(messageText);

        if (maybeDigit !== null) {
          textForNormalization = maybeDigit;
        }
      }
    }

    // typed-safe mapping: "five" -> "5" (but no "to"->2 here anymore)
    const normalizedText = normalizeSuds(textForNormalization);

    const homeworkVoiceChoices = getHomeworkVoiceChoices({
      text: lastAssistant?.text,
      intent: lastAssistant?.intent,
      isQAStep: lastAssistant?.isQAStep,
      awaitKey: pendingAwaitKey,
    });

    // ✅ Homework-choice digit (for system bubble + Base64 selectHomework cue)
    // Handles voice: "three", "three.", "too", "tu", etc.  Handles typed: "3", "3."
    const homeworkPickDigit = (() => {
      // Normalize again at the actual send boundary. This also covers speech
      // that arrived before the Live router had its latest selection context.
      if (fromRealtimeVoice && homeworkVoiceChoices.length) {
        return parseHomeworkVoiceSelection(messageText, homeworkVoiceChoices);
      }
      if (pendingAwaitKey !== "homework_choice") return null;

      const raw = (messageText || "").trim();

      // Strip leading/trailing punctuation (e.g., "3.", "three.")
      const cleaned = raw.toLowerCase().replace(/^[^\w]+|[^\w]+$/g, "");

      const token = fromRealtimeVoice
        ? (normalizeVoiceSingleWordDigit(raw) ?? normalizeSuds(cleaned))
        : normalizeSuds(cleaned);

      return token.trim().match(/^(\d{1,2})\b/)?.[1] ?? null;
    })();

    const isQATurn =
      lastAssistant?.isQAStep === true || lastAssistantIsQAStepRef.current;

    debugLog("🧠 [Q&A][CONSUME_CHECK]", {
      isQATurn,
      before_consume_ref: lastAssistantIsQAStepRef.current,
      text_before_append: normalizedText,
      ts: performance.now(),
    });

    if (isQATurn) {
      lastAssistantIsQAStepRef.current = false;

      debugLog("🧠 [Q&A][CONSUMED]", {
        after_consume_ref: lastAssistantIsQAStepRef.current,
      });
    }

    let dictationOrigin = false;

    // GPT-Live owns spoken output for delegated turns.
    // Ask OCD v4 for text so we don't generate duplicate ElevenLabs audio.
    let type: "text" | "audio" =
      gptLiveOwnsOutput
        ? "text"
        : getOutboundType();

    if (typeof arg === "boolean") dictationOrigin = arg;
    else if (arg?.dictationOrigin) dictationOrigin = true;

    const lastMode = lastAssistant?.mode || null;

    // Are we in a step-based flow already?
    const inStepFlow =
      lastMode === "HOMEWORK_GUIDANCE" ||
      lastMode === "EXPOSURE_COLLECTION";

    // ----------------------------------------------
    // NEW unified systemText logic
    // ----------------------------------------------

    const phase = detectSystemPhase(messageText, lastAssistant?.text);

    debugLog("🧭 System phase decision", {
      phase,
      pendingAwaitKey,
      lastMode,
    });

    const DEFAULT_SYSTEM_TEXT = "One sec — processing your request.";

    let systemText = DEFAULT_SYSTEM_TEXT;
    // 0) Coping skills lookup (first-turn, deterministic, FastTrack-aligned)
    const normalizedForIntent = normalizeForIntent(messageText);

    if (
      !pendingAwaitKey &&
      COPING_QUERY_PHRASES.has(normalizedForIntent)
    ) {
      systemText = "One sec — retrieving your coping skills.";
    }

    // 1) Highest priority: user submitted the ALTERED answer
    if (pendingAwaitKey === "ALTERED") {
      if (lastMode === "HOMEWORK_GUIDANCE") {
        // Assigned homework
        systemText = "One moment — saving your homework details.";
      } else if (lastMode === "EXPOSURE_COLLECTION") {
        // Self-initiated exposure
        systemText = "One moment — saving your self-initiated exposure details.";
      } else {
        // Fallback if mode is missing/unknown
        systemText = "One moment — saving your session details.";
      }
    }

    // 2) Phase-based saving (assigned vs self-initiated)
    else if (phase === "saveHomework") {
      systemText = "One moment — saving your homework details.";
    }
    else if (phase === "saveExposure") {                        // more investigation needed for saveExposure
      systemText = "One sec while I pull up your next step.";
    }

    // 3) Choosing a homework item
    else if (pendingAwaitKey === "homework_choice") {
      if (homeworkPickDigit != null) {
        systemText = "One sec while I retrieve your selected homework.";
      }
    }

    // 4) Timer steps (“ready”)
    else if (
      normalizedText.trim().toLowerCase() === "ready" ||
      normalizedText.trim().toLowerCase().startsWith("ready ")
    ) {
      systemText = "One sec while I pull up the timer.";
    }

    // 5) Homework retrieval (user-initiated, send-time)
    else if (phase === "searchHomework") {
      systemText = "One sec while I retrieve your homework.";
    }

    // 6) Normal step-based flow
    else if (inStepFlow) {
      systemText = "One sec while I pull up your next step.";
    }

    // 🔊 System pre-recorded cues (independent of realtime voice; only depends on Play Audio toggle)
    if (!gptLiveOwnsOutput && getOutboundType() === "audio") {
      const b64 = voiceProfile.base64;

      debugLog("🎧 Available system audio clips", {
        voiceKey,
        hasGreeting: Boolean(b64?.greeting),
        hasRetrieveHomework: Boolean(b64?.retrieveHomework),
        hasSaveHomework: Boolean(b64?.saveHomework),
        hasSaveExposure: Boolean(b64?.saveExposure),
        saveClipsSame:
          Boolean(b64?.saveHomework) &&
          Boolean(b64?.saveExposure) &&
          b64!.saveHomework === b64!.saveExposure,
      });

      const isHomeworkPickNumber =
        pendingAwaitKey === "homework_choice" && homeworkPickDigit != null;

      // 1) entering homework retrieval (send-time)
      if (
        phase === "searchHomework" &&
        pendingAwaitKey !== "homework_choice" &&
        b64?.retrieveHomework
      ) {
        debugLog("🎧 PLAY → retrieveHomework");
        void playTts(b64.retrieveHomework, "audio/mpeg", undefined, SYSTEM_TTS_VOLUME);
      }

      // 🔊 Coping skills retrieval (FastTrack-aligned, first-turn)
      else if (
        !pendingAwaitKey &&
        COPING_QUERY_PHRASES.has(normalizedForIntent) &&
        b64?.retrieveCoping
      ) {
        debugLog("🎧 PLAY → retrieveCoping");
        void playTts(b64.retrieveCoping, "audio/mpeg", undefined, SYSTEM_TTS_VOLUME);
      }

      // 2) homework selected (number pressed)
      else if (isHomeworkPickNumber && b64?.selectHomework) {
        debugLog("🎧 PLAY → selectHomework", { homeworkPickDigit, fromRealtimeVoice });
        void playTts(b64.selectHomework, "audio/mpeg", undefined, SYSTEM_TTS_VOLUME);
      }

      // ALTERED → saving step (mutually exclusive)
      else if (pendingAwaitKey === "ALTERED") {
        if (lastMode === "EXPOSURE_COLLECTION" && b64?.saveExposure) {
          debugLog("🎧 PLAY → saveExposure");
          void playTts(b64.saveExposure, "audio/mpeg", undefined, SYSTEM_TTS_VOLUME);
        }
        else if (b64?.saveHomework) {
          // default fallback
          debugLog("🎧 PLAY → saveHomework (fallback)");
          void playTts(b64.saveHomework, "audio/mpeg", undefined, SYSTEM_TTS_VOLUME);
        }
      }

      // 🔇 Temporarily suppress the generic
      // “One sec — processing your request” audio.
      /*
      else if (
        systemText === DEFAULT_SYSTEM_TEXT &&
        !pendingAwaitKey &&
        phase === "generic" &&
        b64?.processRequest
      ) {
        debugLog("🎧 PLAY → processRequest");
        void playTts(
          b64.processRequest,
          "audio/mpeg",
          undefined,
          SYSTEM_TTS_VOLUME
        );
      }
      */

    }

    setMessages((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        text: messageText,
        sender: "user",
        timestamp: new Date(),
      },

      // Temporarily hide selected processing messages.
      // Preserve more informative system messages.
      ...(
        systemText === DEFAULT_SYSTEM_TEXT ||
          systemText === "One sec while I pull up your next step."
          ? []
          : [
            {
              id: String(Date.now()) + "-sys",
              text: systemText,
              sender: "system" as const,
              timestamp: new Date(),
            },
          ]),
    ]);

    // one-shot awaitKey pass-through
    const keyForThisSend = pendingAwaitKey || (
      fromRealtimeVoice && homeworkPickDigit != null && homeworkVoiceChoices.length
        ? "homework_choice" : undefined
    );
    if (keyForThisSend) setPendingAwaitKey(null);

    // one-shot signal pass-through
    const signalsForThisSend = pendingSignals || undefined;
    if (signalsForThisSend) setPendingSignals(null);

    // 🆕 Outbound text that we actually send to OCD v4
    let outboundText = normalizedText;

    // ✅ FIX: realtime voice homework choice should send the digit, not the raw transcript
    if (
      fromRealtimeVoice &&
      keyForThisSend === "homework_choice" &&
      homeworkPickDigit != null
    ) {
      outboundText = homeworkPickDigit;
    }

    // ✅ FIX: realtime voice coping choice should send the digit, not the raw transcript
    if (
      fromRealtimeVoice &&
      keyForThisSend === "coping_choice"
    ) {
      const d = normalizeVoiceSingleWordDigit(messageText);
      if (d != null) outboundText = d;
    }

    if (isQATurn) {
      const lower = outboundText.trim().toLowerCase();

      // don’t double-append if user already typed a completion word
      const hasCompletionWord =
        /\b(done|finished|finish|complete|completed|all set|that's it)\b/.test(lower);

      if (!hasCompletionWord) {
        const base = outboundText.trim();

        // ✅ Preserve the actual answer; add completion marker on its own line
        // This avoids the "answer finished" parsing defect.
        outboundText = base ? `${base}\n\nfinished` : "finished";
      }

      debugLog("[Q&A] outboundText length:", outboundText.length);
    }

    const sidAtSend = sessionId;
    setIsLoading(true);

    try {
      // 🛡️ Patch C: block prompt/context leaks BEFORE calling n8n
      const trimmedOutbound = (outboundText || "").trim();
      const looksLikePromptLeak =
        trimmedOutbound.startsWith("context:") ||
        trimmedOutbound.includes("###") ||
        trimmedOutbound.includes("Transcribe the user's speech") ||
        trimmedOutbound.includes("Transcribe in English") ||
        trimmedOutbound.includes("Use ASCII characters") ||
        trimmedOutbound.includes("Arabic digits");

      if (looksLikePromptLeak) {
        console.warn("[efa-chat] blocked prompt-leak outboundText", {
          preview: trimmedOutbound.slice(0, 160),
        });

        setMessages((prev) => [
          ...prev,
          {
            id: String(Date.now()) + "-sys-leak",
            text: "I didn’t catch that—could you please repeat?",
            sender: "system",
            timestamp: new Date(),
          },
        ]);

        return;
      }

      const resp = await sendMessageToWebhook(
        outboundText,           /// 🆕 use outboundText
        type,
        dictationOrigin,
        fromRealtimeVoice,     // ✅ PASS IT
        keyForThisSend,        // 👈 pass the key
        signalsForThisSend    // 👈 pass signals
      );

      // ✅ If the user hit "New chat" while this request was in-flight,
      // drop the stale response instead of appending it to the new chat.
      if (!isActiveSession(sidAtSend)) return;

      debugLog("🧠 [Q&A][ARM]", {
        resp_isQAStep: resp.isQAStep,
        before_arm_ref: lastAssistantIsQAStepRef.current,
        outboundTextLen: outboundText.length,
        ts: performance.now(),
      });

      // 🧠 ARM Q&A BATON IMMEDIATELY (before any UI/audio)
      lastAssistantIsQAStepRef.current = resp.isQAStep === true;

      debugLog("🧠 [Q&A][ARM_AFTER]", {
        after_arm_ref: lastAssistantIsQAStepRef.current,
      });

      // keep local selection
      if (resp.selectedHomework) setSelectedHomework(resp.selectedHomework);

      if (typeof resp.homeworkMode === "boolean") {
        setInHomework(resp.homeworkMode);
      } else if (resp.selectedHomework) {
        setInHomework(true);
      } else if (resp.homeworkMode == null) {
        setInHomework(false);
      }

      setMessages((prev) => {
        const assistantId = (Date.now() + 1).toString();

        const next: MessageWithArtifact[] = [
          ...prev,
          {
            id: assistantId,
            text: resp.text,
            attachments: resp.attachments,
            sender: "assistant",
            timestamp: new Date(),
            markdown: true,
            timer: resp.timer,
            sudsControl: resp.sudsControl ?? null,
            confetti: resp.confetti,
            artifact: resp.artifact || null,
            mode: resp.mode || null,
            intent: (resp.intent as Message["intent"]) || undefined,
            hideQuick: resp.intent === "COPING_GUIDE",
            isQAStep: resp.isQAStep === true,
            signals: resp.signals ?? undefined,
            pointsEarned: resp.pointsEarned,
            coinsEarned: resp.coinsEarned,
            coachPrompts: Array.isArray(resp.coachPrompts) ? resp.coachPrompts : [],
            coachSkill: resp.coachSkill || null,
          },
        ];

        // ✅ Mobile-only: insert GIF row immediately AFTER the congrats message
        if (resp.confetti && shouldUseInlineRewardGif()) {
          const sig = `inline-reward:${assistantId}:${resp.pointsEarned ?? 0}:${resp.coinsEarned ?? 0}`;
          if (lastInlineRewardSigRef.current !== sig) {
            lastInlineRewardSigRef.current = sig;

            pendingRewardScrollRef.current = true; // ✅ ADD

            next.push({
              id: `${assistantId}-reward-gif`,
              sender: "assistant",
              timestamp: new Date(),
              text: "",               // not used (we render via rewardGif flag)
              markdown: false,
              rewardGif: true,
            } as MessageWithArtifact);
          }
        }

        return next;
      });

      // 🔊 Legacy assistant audio.
      // GPT-Live delegated turns use session.commentary.append instead.
      if (!gptLiveOwnsOutput && getOutboundType() === "audio") {
        await playTts(
          resp.audioBase64,
          resp.mimeType,
          resp.audioUrl,
          1.0
        );
      }

      // 👇 insert this
      if (resp.awaitKey) {
        setPendingAwaitKey(resp.awaitKey);
      }

      // 🎙 GPT-Live client delegation:
      // return OCD v4's authoritative response to the same Live delegation.
      //
      // When OCD v4 returns a timer plus coachPrompts, the coaching tips
      // are visually rendered separately from resp.text. Explicitly include
      // them in GPT-Live commentary so voice and visual UI stay in sync.
      if (
        gptLiveOwnsOutput &&
        (!fromGptLive || Boolean(gptLiveDelegationId))
      ) {
        const gptLiveReplyParts: string[] = [];

        if (resp.text?.trim()) {
          gptLiveReplyParts.push(
            resp.text.trim()
          );
        }

        if (
          resp.timer &&
          Array.isArray(resp.coachPrompts) &&
          resp.coachPrompts.length > 0
        ) {
          gptLiveReplyParts.push(
            "Here are some coaching tips to keep in mind:"
          );

          for (const prompt of resp.coachPrompts) {
            const tip =
              String(prompt || "").trim();

            if (tip) {
              gptLiveReplyParts.push(tip);
            }
          }
        }

        const gptLiveReply =
          sanitizeForGptLiveSpeech(
            gptLiveReplyParts.join("\n")
          );

        const commentarySent =
          gptLiveVoice.sendCommentary(
            fromGptLive ? gptLiveDelegationId : null,
            gptLiveReply
          );

        if (!commentarySent) {
          console.warn(
            "[GPT-Live] failed to send OCD v4 reply back to Live",
            {
              delegationId:
                gptLiveDelegationId,
              hasReply:
                Boolean(gptLiveReply),
              coachPromptCount:
                Array.isArray(resp.coachPrompts)
                  ? resp.coachPrompts.length
                  : 0,
            }
          );
        }
      }

      // 🧭 Store transient signals for next turn (lossless round-trip)
      if (resp.signals && Object.keys(resp.signals).length > 0) {
        setPendingSignals(resp.signals);
      }

      //  setSudsEnforce(false);
    } finally {
      // ✅ Only clear loading for the session that initiated the request.
      if (isActiveSession(sidAtSend)) setIsLoading(false);
    }
  };

  // 🔁 Keep handleSendMessageRef synced with latest function
  useEffect(() => {
    handleSendMessageRef.current = handleSendMessage;
  }, [handleSendMessage]);

  // ─────────────────────────────────────────────────────────────
  // Committed UI action sender
  //
  // Use this for buttons/controls whose click represents a complete
  // answer to the assistant.
  //
  // Important for realtime voice:
  // cancel any transcript waiting in the 900ms trailing buffer before
  // sending the button answer, otherwise a partial spoken phrase could
  // flush immediately after the click.
  // ─────────────────────────────────────────────────────────────
  const sendUiAction = useCallback(
    (value: string) => {
      const text = (value || "").trim();
      if (!text) return;

      if (voiceSendTimerRef.current) {
        window.clearTimeout(voiceSendTimerRef.current);
        voiceSendTimerRef.current = null;
      }

      voiceFinalBufferRef.current = "";

      void handleSendMessage(text, {
        fromUiAction: true,
      });
    },
    [handleSendMessage]
  );

  // timer events (unchanged)
  const handleTimerEvent = (
    event: "timeUp" | "finishedEarly" | "cantFinish" | "paused" | "resumed" | "extended" | "elapsed",
    label?: string,
    duration?: number,
    remaining?: number,
    elapsed?: number
  ): void => {
    resetActivitySafe(); // reset nudge timer

    // If this timer action originated from GPT-Live, capture the
    // delegation now. Clear the shared ref immediately so it cannot
    // accidentally leak into a later timer event.
    const gptLiveTimerDelegationId =
      pendingGptLiveTimerDelegationRef.current;

    if (gptLiveTimerDelegationId) {
      pendingGptLiveTimerDelegationRef.current =
        null;
    }

    (async () => {

      // 🧩 Hide quick replies while processing timer events (prevents "Ready" flicker)
      if (["finishedEarly", "timeUp", "cantFinish", "elapsed"].includes(event)) {
        setSuppressQuickReplies(true);
      }

      setMessages((prev) => [
        ...prev,
        {
          id: String(Date.now()) + "-sys",
          text: `⏳ Timer ${event}${label ? `: ${label}` : ""}…`,
          sender: "system",
          timestamp: new Date(),
        },
      ]);

      {
        let batonText = "";

        if (event === "finishedEarly") {
          batonText = "You marked this step finished early.";
        } else if (event === "elapsed") {
          batonText = "You marked this step finished.";
        } else if (event === "timeUp") {
          batonText = "The timer has reached zero.";
        } else if (event === "paused") {
          batonText = "Timer paused. Tap Resume when you’re ready.";
        } else if (event === "resumed") {
          batonText = "Timer resumed.";
        }

        if (batonText) {
          setMessages((prev) => [
            ...prev,
            {
              id: Date.now().toString(),
              text: batonText,
              sender: "assistant",
              timestamp: new Date(),
              markdown: false,
            },
          ]);
        }
      }

      const r =
        typeof remaining === "number" && Number.isFinite(remaining)
          ? Math.max(0, Math.floor(remaining))
          : undefined;
      const e =
        typeof elapsed === "number" && Number.isFinite(elapsed)
          ? Math.max(0, Math.floor(elapsed))
          : undefined;

      // ———————————————————————————
      // 1) Update ended timer messages
      // ———————————————————————————
      if (["finishedEarly", "cantFinish", "timeUp", "elapsed"].includes(event)) {

        setMessages(prev => {
          // find last active timer message index
          const idx = [...prev].reverse().findIndex(m => m.timer && !m.timer.ended);
          if (idx === -1) return prev;
          const realIdx = prev.length - 1 - idx;

          return prev.map((m, i) => {
            if (i !== realIdx || !m.timer) return m;
            return { ...m, timer: { ...m.timer, ended: true, remaining: r, elapsed: e } };
          });
        });

        // 2) Acknowledge inability to finish without claiming completion.
        if (event === "cantFinish") {
          setMessages((prev) => [
            ...prev,
            {
              id: Date.now().toString(),
              text: "You marked that you couldn’t finish this step.",
              sender: "assistant",
              timestamp: new Date(),
              markdown: false,
            },
          ]);
        }

        // ———————————————————————————
        // 3) Pull up next step
        // Temporarily disabled.
        // ———————————————————————————
        /*
        setMessages((prev) => [
          ...prev,
          {
            id: String(Date.now()) + "-sys-next",
            text: "One sec while I pull up your next step.",
            sender: "system",
            timestamp: new Date(),
          },
        ]);
        */

        const sidAtSend = sessionId;
        setIsLoading(true);

        // ———————————————————————————
        // 4) Build machine-readable timer baton for n8n
        // ———————————————————————————
        const timerDetails = [
          r != null ? `remaining=${r}s` : null,
          e != null ? `elapsed=${e}s` : null,
        ]
          .filter((part): part is string => part !== null)
          .join(", ");

        const batonForward =
          event === "finishedEarly"
            ? `Timer finishedEarly: ${label ?? "Timer"}${timerDetails ? ` (${timerDetails})` : ""
            }`
            : event === "cantFinish"
              ? `Timer cantFinish: ${label ?? "Timer"}${timerDetails ? ` (${timerDetails})` : ""
              }`
              : event === "elapsed"
                ? `Timer elapsed: ${label ?? "Timer"}${timerDetails ? ` (${timerDetails})` : ""
                }`
                : `Timer timeUp: ${label ?? "Timer"}`;

        try {
          const res = await fetch(requireWebhookUrl(), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              origin: "timer",

              // GPT-Live owns spoken output for GPT-Live timer commands.
              // Ask OCD v4 for text so ElevenLabs is not generated/played.
              type: gptLiveTimerDelegationId
                ? "text"
                : getOutboundType(),

              inputMode: "text",
              deviceType,
              voiceId: voiceProfile.elevenLabsVoiceId,
              chatInput: batonForward,
              accessCode: accessCode || "",
              sessionId: sidAtSend,
            }),
          });

          const raw = await res.text();
          let data: any = {};
          try { data = raw ? JSON.parse(raw) : {}; } catch { }

          // ✅ drop stale timer baton
          if (!isActiveSession(sidAtSend)) return;

          // ✅ Keep multi-item array responses intact.
          // Some n8n flows return: [ { assistant reply... }, { awaitKey / signals... } ]
          const replyCarrier =
            Array.isArray(data)
              ? (data.find(
                (x) =>
                  x &&
                  typeof x === "object" &&
                  ("audioBase64" in x ||
                    "message" in x ||
                    "reply" in x ||
                    "text" in x)
              ) ?? data[0])
              : data;

          const batonCoachPrompts = Array.isArray(pickField(replyCarrier, "coachPrompts"))
            ? pickField(replyCarrier, "coachPrompts")
            : [];

          // ✅ ARM Q&A baton for timer-forwarded assistant replies
          const batonIsQAStep = pickBool(data, "isQAStep") ?? false;
          lastAssistantIsQAStepRef.current = batonIsQAStep === true;

          debugLog("🧠 [Q&A][ARM_TIMER]", {
            batonIsQAStep,
            after_arm_ref: lastAssistantIsQAStepRef.current,
          });

          const replyText =
            extractReplyText(replyCarrier) ||
            extractReplyText(data) ||
            "Message received!";

          const batonArtifact =
            extractArtifact(replyCarrier) ||
            extractArtifact(data) ||
            undefined;

          const batonTimer =
            extractTimer(replyCarrier) ||
            extractTimer(data) ||
            undefined;

          const batonSudsControl =
            extractSudsControl(replyCarrier) ??
            extractSudsControl(data) ??
            null;

          const batonConfetti = Boolean(
            pickField(replyCarrier, "confetti") ??
            pickField(data, "confetti")
          );

          const batonMode =
            pickStr(replyCarrier, "mode") ||
            pickStr(data, "mode") ||
            pickStr(data, "state.mode") ||
            pickStr(data, "parameters.mode");

          const batonAudioBase64 = pickStr(replyCarrier, "audioBase64") ?? pickStr(data, "audioBase64");
          const batonMimeType = pickStr(replyCarrier, "mimeType") ?? pickStr(data, "mimeType");
          const batonAudioUrl = pickStr(replyCarrier, "audioUrl") ?? pickStr(data, "audioUrl");

          const batonAwaitKey =
            pickStr(data, "awaitKey") ||
            undefined;

          debugLog("🎧 [timer baton] about to play:", {
            audioUrl: batonAudioUrl,
            mimeType: batonMimeType,
            base64Len: batonAudioBase64?.length ?? 0,
            outboundType: getOutboundType(),
          });

          // 🔊 Legacy assistant audio.
          //
          // GPT-Live timer turns return text through session.commentary.append,
          // so do not also play ElevenLabs.
          if (
            !gptLiveTimerDelegationId &&
            getOutboundType() === "audio"
          ) {
            await playTts(
              batonAudioBase64,
              batonMimeType,
              batonAudioUrl,
              1.0
            );
          }

          setMessages(prev => {
            const assistantId = Date.now().toString();

            const next: MessageWithArtifact[] = [
              ...prev,
              {
                id: assistantId,
                text: replyText,
                sender: "assistant",
                timestamp: new Date(),
                markdown: true,
                timer: batonTimer,
                sudsControl: batonSudsControl,
                confetti: batonConfetti,
                artifact: batonArtifact || null,
                mode: batonMode || null,
                isQAStep: batonIsQAStep === true,
                coachPrompts:
                  batonTimer
                    ? batonCoachPrompts
                    : [],

                // ✅ rewards if present in response
                pointsEarned: toNumber(pickField(data, "pointsEarned")),
                coinsEarned: toNumber(pickField(data, "coinsEarned")),
              },
            ];

            // ✅ Mobile-only: insert GIF row immediately AFTER the congrats message
            if (batonConfetti && shouldUseInlineRewardGif()) {
              const sig = `inline-reward:${assistantId}`;
              if (lastInlineRewardSigRef.current !== sig) {
                lastInlineRewardSigRef.current = sig;

                pendingRewardScrollRef.current = true; // ✅ ADD

                next.push({
                  id: `${assistantId}-reward-gif`,
                  sender: "assistant",
                  timestamp: new Date(),
                  text: "",
                  markdown: false,
                  rewardGif: true,
                } as MessageWithArtifact);
              }
            }

            return next;
          });

          // Preserve the orchestration baton returned after a timer event.
          // Important when the next step is SUDS2.
          if (batonAwaitKey) {
            setPendingAwaitKey(batonAwaitKey);
          }

          // 🎙 GPT-Live timer delegation:
          //
          // Return OCD v4's authoritative next response to the same
          // GPT-Live delegation that triggered Finished / Finished Early /
          // Can't Finish.
          if (gptLiveTimerDelegationId) {
            const sendCommentary =
              gptLiveSendCommentaryRef.current;

            if (sendCommentary) {
              const timerReplyParts: string[] = [];

              if (replyText?.trim()) {
                timerReplyParts.push(
                  replyText.trim()
                );
              }

              // Speak coaching prompts only when the NEW response
              // actually contains a timer.
              //
              // Coaching prompts belong to the timed step and should
              // not carry forward into SUDS2 or the next non-timer turn.
              if (
                batonTimer &&
                Array.isArray(batonCoachPrompts) &&
                batonCoachPrompts.length > 0
              ) {
                timerReplyParts.push(
                  "Here are some coaching tips to keep in mind:"
                );

                for (const prompt of batonCoachPrompts) {
                  const tip =
                    String(prompt || "").trim();

                  if (tip) {
                    timerReplyParts.push(tip);
                  }
                }
              }

              const commentaryText =
                sanitizeForGptLiveSpeech(
                  timerReplyParts.join("\n")
                );

              const commentarySent =
                sendCommentary(
                  gptLiveTimerDelegationId,
                  commentaryText
                );

              if (!commentarySent) {
                console.warn(
                  "[GPT-Live][Timer] failed to return timer result",
                  {
                    delegationId:
                      gptLiveTimerDelegationId,
                    hasReply:
                      Boolean(commentaryText),
                  }
                );
              }
            }
          }
        } catch (err) {
          console.error("[Timer baton forward] failed:", err);
        } finally {
          // ✅ Only clear loading/suppression for the session that initiated the baton.
          if (isActiveSession(sidAtSend)) {
            setIsLoading(false);
            setSuppressQuickReplies(false);
          }
        }

        return;
      }

    })();
  };

  useEffect(() => {
    if (messages.length === 0) return;
    if (isMobile) return;

    // Do not pull keyboard focus into text chat
    // while GPT-Live is connected.
    if (ENABLE_GPT_LIVE_1 && gptLiveVoice.isConnected) {
      return;
    }

    const last = messages[messages.length - 1];

    if (last.sender === "assistant" || last.sender === "system") {
      setTimeout(() => {
        if (!ENABLE_GPT_LIVE_1 || !gptLiveVoice.isConnected) {
          chatInputRef.current?.focus();
        }
      }, 0);
    }
  }, [messages, isMobile, gptLiveVoice.isConnected]);

  useEffect(() => {
    if (messages.length === 0) return;

    const last = messages[messages.length - 1];
    if (!last?.confetti) return;

    // ✅ prevent replay on rerender/history
    const sig = `celebrate:${last.id}`;
    if (lastCelebrationSigRef.current === sig) return;
    lastCelebrationSigRef.current = sig;

    debugLog("🎉 Confetti triggered!");
    fireConfetti();

    // ✅ show reward overlay shortly after confetti starts (no confetti.ts changes)
    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

    if (!reduceMotion) {
      // ✅ Desktop: keep overlay behavior
      if (!isMobile) {
        window.setTimeout(() => {
          setShowRewardOverlay(true);
        }, 700);
      }
      // ✅ Mobile: do nothing here; we render inline GIF in the messages loop
    }

    // 🆕 Apply reward deltas (your existing code unchanged)
    const pointsDelta = toNumber((last as any).pointsEarned);
    const coinsDelta = toNumber((last as any).coinsEarned);

    const rewardSig = `${pointsDelta}:${coinsDelta}:${last.id}`;
    if (lastRewardSigRef.current === rewardSig) return;
    lastRewardSigRef.current = rewardSig;

    if (pointsDelta > 0) setPointsEarned((prev) => prev + pointsDelta);
    if (coinsDelta > 0) setCoins((prev) => prev + coinsDelta);

  }, [messages]);

  const latestAssistant = useMemo(
    () => [...messages].reverse().find((m) => m.sender === "assistant") || null,
    [messages]
  );

  // Structured SUDS control is active only when it belongs to the
  // current assistant turn AND its awaitKey matches the baton we're holding.
  // This prevents an old custom SUDS control from remaining active after the turn advances.
  const activeSudsControl = useMemo(() => {
    const control = latestAssistant?.sudsControl ?? null;

    if (!control) return null;
    if (latestAssistant?.isQAStep === true) return null;

    const expectedAwaitKey =
      String(control.awaitKey ?? "")
        .trim()
        .toLowerCase();

    const currentAwaitKey =
      String(pendingAwaitKey ?? "")
        .trim()
        .toLowerCase();

    if (!currentAwaitKey) return null;
    if (currentAwaitKey !== expectedAwaitKey) return null;

    return control;
  }, [
    latestAssistant,
    pendingAwaitKey,
  ]);

  const hasCustomSudsControl =
    activeSudsControl?.style === "slider" ||
    activeSudsControl?.style === "gauge" ||
    activeSudsControl?.style === "emoji";

  // Detect whether the current coping-loop SUDS prompt also has an
  // interactive coping practice attached to the same assistant turn.
  const latestArtifactType =
    String(latestAssistant?.artifact?.type ?? "");

  const latestAssistantHasCopingPracticeUi =
    Boolean(
      latestAssistant &&
      (
        (
          latestAssistant.intent === "COPING_GUIDE" &&
          latestAssistant.mode === "GUIDE" &&
          latestAssistant.coachSkill
        ) ||
        latestArtifactType === "coping_buddy" ||
        latestArtifactType === "abc_game" ||
        latestArtifactType === "target_game" ||
        latestArtifactType === "html_activity"
      )
    );

  // Delay all structured SUDS entry styles during an interactive coping practice.
  // Normal SUDS1 and SUDS2 remain unchanged.
  const shouldDelayCopingLoopSuds =
    activeSudsControl?.stage === "coping_loop" &&
    latestAssistantHasCopingPracticeUi &&
    copingPracticeFinishedMessageId !== latestAssistant?.id;

  // Number Buttons use QuickActionsBar rather than SudsInput.
  // Allow them back after the patient finishes the coping practice.
  const showCopingLoopSudsNumberButtons =
    activeSudsControl?.stage === "coping_loop" &&
    activeSudsControl?.style === "buttons" &&
    latestAssistantHasCopingPracticeUi &&
    copingPracticeFinishedMessageId === latestAssistant?.id;

  // Base quick actions from assistant text + special awaited-response actions
  const quickActions = useMemo<QuickAction[]>(() => {
    if (!latestAssistant?.text) return [];

    // Structured Number Buttons SUDS should not rely on text parsing.
    // Generate the full 0–10 scale directly from sudsControl.
    if (activeSudsControl?.style === "buttons") {
      const min = Number.isFinite(activeSudsControl.min)
        ? activeSudsControl.min
        : 0;

      const max = Number.isFinite(activeSudsControl.max)
        ? activeSudsControl.max
        : 10;

      const step =
        Number.isFinite(activeSudsControl.step) &&
          activeSudsControl.step > 0
          ? activeSudsControl.step
          : 1;

      const actions: QuickAction[] = [];

      for (let value = min; value <= max; value += step) {
        const label = String(value);

        actions.push({
          id: `suds-${label}`,
          label,
          value: label,
        });
      }

      return actions;
    }

    // ALTERED closeout: show three fixed responses.
    // The normal ChatInput remains visible for a custom typed response.
    const isAwaitingAltered =
      (pendingAwaitKey ?? "").trim().toUpperCase() === "ALTERED";

    if (isAwaitingAltered) {
      return [
        {
          id: "altered-none",
          label: "No changes",
          value: "no changes",
        },
        {
          id: "altered-didnt-finish",
          label: "Didn’t finish",
          value: "didn't finish",
        },
        {
          id: "altered-completed-with-modification",
          label: "Completed with modification",
          value: "completed with modification",
        },
      ] as QuickAction[];
    }

    // Start with the normal extractor for SUDS, homework, coping, etc.
    const extractedBase = extractQuickActionsFromText(latestAssistant.text, {
      awaitKey: pendingAwaitKey ?? null,
    });

    // HTML activities own their internal numeric interactions.
    // Suppress only text-inferred numeric scale buttons such as 1–10.
    // Keep Finished / Confirm and all other quick actions.
    const base =
      latestArtifactType === "html_activity"
        ? extractedBase.filter(
          (action) =>
            action.group !== "SUDS" &&
            action.group !== "Scale0-10"
        )
        : extractedBase;

    // 2) Add coping-selection numbers when OCD v4 says:
    //    mode: "SUGGESTION", intent: "COPING_QUERY", awaitKey: "coping_choice"
    const needCopingNumbers =
      String(latestAssistant.intent) === "COPING_QUERY" &&
      pendingAwaitKey === "coping_choice" &&
      !base.some((a) => a.group === "CopingSelect");

    if (!needCopingNumbers) return base;

    const lines = latestAssistant.text.split(/\r?\n/);
    const enumRx = /^\s*(?:[-*]\s*)?(\d{1,2})[.)]\s+(.+?)\s*$/;

    const copingActions: QuickAction[] = [];

    for (const line of lines) {
      const m = line.match(enumRx);
      if (m) {
        const n = m[1].trim(); // "1", "2", ...
        copingActions.push({
          id: `coping-${n}`,
          label: n,
          value: n,
          group: "CopingSelect",
        });
      }
    }

    if (!copingActions.length) return base;

    // 3) De-dupe base + coping by (label, value)
    const seen = new Set<string>();
    const merged: QuickAction[] = [];
    for (const a of [...base, ...copingActions]) {
      const key = `${a.label.toLowerCase()}::${a.value.toLowerCase()}`;
      if (!seen.has(key)) {
        seen.add(key);
        merged.push(a);
      }
    }
    return merged;
  }, [
    latestAssistant,
    pendingAwaitKey,
    activeSudsControl,
    latestArtifactType,
  ]);

  // Structured sudsControl is authoritative when present.
  // During an interactive coping practice, defer SUDS UI/input mode
  // until the patient indicates they are done practicing.
  const sudsFromPrompt = useMemo(() => {
    if (latestAssistant?.isQAStep) return false;

    if (shouldDelayCopingLoopSuds) {
      return false;
    }

    return (
      Boolean(activeSudsControl) ||
      isSudsPrompt(latestAssistant?.text || "")
    );
  }, [
    latestAssistant,
    activeSudsControl,
    shouldDelayCopingLoopSuds,
  ]);

  // ✅ Minimal: detect when the assistant is asking you to choose a homework number
  const isHomeworkChoice = useMemo(() => {
    const txt = (latestAssistant?.text || "").toLowerCase();
    return (
      /\bchoose (a )?homework\b/i.test(txt) ||
      /\bchoose a homework number\b/i.test(txt) ||
      /please choose.*number/i.test(txt) ||
      pendingAwaitKey === "homework_choice" ||
      latestAssistant?.intent === "HW_SELECTION"
    );
  }, [latestAssistant, pendingAwaitKey]);

  // Hide numeric-only chips unless it's a real SUDS prompt,
  // but NEVER hide HomeworkSelect or CopingSelect options.
  const displayActions = useMemo(() => {
    if (!quickActions.length) return quickActions;

    const hasHomeworkSelect = quickActions.some(
      (a) => a.group === "HomeworkSelect"
    );
    const hasCopingSelect = quickActions.some(
      (a) => a.group === "CopingSelect"
    );

    // If this is a homework choice or coping-skills choice, always show them.
    if (hasHomeworkSelect || hasCopingSelect) return quickActions;

    const numericGroup = isSudsGroup(quickActions);

    // Slider/Gauge/Emoji replace only the legacy SUDS number-button row.
    // Do not affect homework/coping numeric choices.
    if (hasCustomSudsControl && numericGroup) {
      return [];
    }

    let out = !sudsFromPrompt && numericGroup ? [] : quickActions;

    // ✅ HARD RULE: Only show the "None" chip when we're explicitly awaiting COPING or ALTERED.
    // This prevents homework Q&A steps ("If this does not apply, type 'none'") from generating a chip.
    const ak = (pendingAwaitKey ?? "").trim().toUpperCase();
    const allowNoneChip = ak === "COPING" || ak === "ALTERED";

    if (!allowNoneChip) {
      out = out.filter((a) => {
        const v = (a.value ?? "").trim().toLowerCase();
        const l = (a.label ?? "").trim().toLowerCase();
        const id = (a.id ?? "").trim().toLowerCase();
        return v !== "none" && l !== "none" && id !== "none";
      });
    }

    // ✅ Q&A guard: never show "None" quick action on Q&A steps.
    // (Homework steps may include "type 'none'" as part of the prompt, but we don't want a chip.)
    if (latestAssistant?.isQAStep === true) {
      out = out.filter(
        (a) => a.id !== "none" && a.value.trim().toLowerCase() !== "none"
      );
    }

    // ✅ Existing: hide Finished only during realtime-voice + Q&A
    const isRealtimeVoiceQa =
      ENABLE_REALTIME_VOICE &&
      Boolean(realtimeVoice?.isActive) &&
      latestAssistant?.isQAStep === true;

    if (isRealtimeVoiceQa) {
      out = out.filter(
        (a) => a.id !== "finished" && a.value.trim().toLowerCase() !== "finished"
      );
    }

    return out;
  }, [
    quickActions,
    sudsFromPrompt,
    latestAssistant,
    realtimeVoice,
    pendingAwaitKey,
    hasCustomSudsControl,
  ]);

  // Keep a choice tied to this authoritative list through the loading render.
  // A new chat or assistant response gets a different scope and cannot reuse it.
  const currentHomeworkVoiceChoices = getHomeworkVoiceChoices({
    text: latestAssistant?.text,
    intent: latestAssistant?.intent,
    isQAStep: latestAssistant?.isQAStep,
    awaitKey: pendingAwaitKey,
  });
  gptLiveInputContextRef.current = {
    scopeKey: `${sessionId}:${latestAssistant?.id ?? "initial"}`,
    homeworkChoices: currentHomeworkVoiceChoices.length
      ? currentHomeworkVoiceChoices : undefined,
  };

  useEffect(() => {
    setSudsEnforce(sudsFromPrompt);
  }, [sudsFromPrompt]);


  const hasVisibleTimer = useMemo(
    () => messages.some((m) => m.timer && !m.timer.ended),
    [messages]
  );

  // ------------------------------------------------------
  // GPT-Live reconnect rehydration snapshot
  //
  // Do NOT replay the whole conversation.
  // Give a replacement Live session only the current
  // authoritative application state required to continue.
  // ------------------------------------------------------
  useEffect(() => {
    const hasConversationState =
      messages.some(
        (m) =>
          m.sender === "user" ||
          m.sender === "assistant"
      ) ||
      Boolean(pendingAwaitKey) ||
      inHomework ||
      hasVisibleTimer;

    // Fresh chat: there is nothing to restore.
    if (!hasConversationState) {
      gptLiveRehydrationContextRef.current = "";
      return;
    }

    const assistantText =
      String(latestAssistant?.text ?? "")
        .trim()
        .slice(0, 900);

    const mode =
      String(latestAssistant?.mode ?? "")
        .trim();

    const intent =
      String(latestAssistant?.intent ?? "")
        .trim();

    const artifactType =
      String(
        latestAssistant?.artifact?.type ?? ""
      ).trim();

    const artifactTitle =
      String(
        (latestAssistant?.artifact as any)?.title ??
        (latestAssistant?.artifact as any)?.label ??
        ""
      ).trim();

    const sudsStage =
      String(
        latestAssistant?.sudsControl?.stage ?? ""
      ).trim();

    const sudsAwaitKey =
      String(
        latestAssistant?.sudsControl?.awaitKey ?? ""
      ).trim();

    const lines = [
      "CONTINUATION STATE FROM THE OCD HOMEWORK ASSISTANT APPLICATION.",
      "This is state context, not a new user message.",
      "Do not greet the user or speak this snapshot aloud.",
      "Use it only to understand the next user utterance.",
      "OCD v4 / client delegation remains authoritative for workflow and clinical logic.",

      `Homework flow active: ${inHomework ? "yes" : "no"}`,

      pendingAwaitKey
        ? `Currently awaiting: ${pendingAwaitKey}`
        : null,

      mode
        ? `Current mode: ${mode}`
        : null,

      intent
        ? `Current intent: ${intent}`
        : null,

      latestAssistant?.isQAStep === true
        ? "Current step is a Q&A step."
        : null,

      hasVisibleTimer
        ? "A homework timer is currently active in the UI."
        : null,

      artifactType
        ? `Active UI activity: ${artifactType}${artifactTitle ? ` (${artifactTitle})` : ""}`
        : null,

      sudsStage
        ? `Active SUDS stage: ${sudsStage}`
        : null,

      sudsAwaitKey
        ? `SUDS await key: ${sudsAwaitKey}`
        : null,

      assistantText
        ? `Latest authoritative assistant message:\n${assistantText}`
        : null,
    ];

    gptLiveRehydrationContextRef.current =
      lines
        .filter(
          (line): line is string =>
            Boolean(line)
        )
        .join("\n");

  }, [
    messages,
    latestAssistant,
    pendingAwaitKey,
    inHomework,
    hasVisibleTimer,
  ]);

  useEffect(() => {
    if (!hasVisibleTimer) {
      timerBubbleRef.current = null;
    }
  }, [hasVisibleTimer]);

  // 🔒 Voice Turn Lock (VTL): assistant owns the turn
  const isVoiceTurnLocked = useMemo(() => {
    return hasVisibleTimer || isLoading || showInitOverlay;
  }, [hasVisibleTimer, isLoading, showInitOverlay]);

  // 👇 INSERT THIS BLOCK RIGHT AFTER hasVisibleTimer (and before return)
  // after hasVisibleTimer is computed
  const { resetActivity } = useInactivityWatchdog({
    sessionId,
    accessCode,

    suppressIf: () => {
      const typing = (chatInputRef.current?.getMessage?.() || "").trim().length > 0;
      // ✅ only nudge while actively in homework mode
      if (!inHomework) return true;
      return hasVisibleTimer || isLoading || showInitOverlay || typing;
    },

    onLocalNudge: (msg) =>
      setMessages((prev) => [
        ...prev,
        { id: Date.now().toString(), text: msg, sender: "system", timestamp: new Date() },
      ]),


    onNudgeReply: async (p) => {
      if (getOutboundType() === "audio") {
        await playTts(p.audioBase64, p.mimeType, p.audioUrl, 1.0);
      }

      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          text: p.text,
          sender: "assistant",
          timestamp: new Date(),
          markdown: true,
          confetti: Boolean(p.confetti),
        },
      ]);
    },
  });

  useEffect(() => {
    resetActivityRef.current = resetActivity;
  }, [resetActivity]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    resetActivitySafe(); // reset nudge timer


    const target = e.currentTarget;
    const isBottom =
      target.scrollHeight - target.scrollTop <= target.clientHeight + 50;
    setIsAtBottom(isBottom);
  };

  useEffect(() => {
    hasVisibleTimerRef.current = hasVisibleTimer;
    suspendRealtimeVoiceRef.current = isVoiceTurnLocked; // ✅ VTL => suspend
  }, [hasVisibleTimer, isVoiceTurnLocked]);

  // 🛑 Timer guard: clear any queued trailing flush while timer is running,
  // but DO NOT stop realtime voice (we "suspend" by ignoring transcripts).
  useEffect(() => {
    if (!ENABLE_REALTIME_VOICE) return;

    if (isVoiceTurnLocked) {
      // Clear any queued trailing flush (prevents “late send”)
      if (voiceSendTimerRef.current) {
        window.clearTimeout(voiceSendTimerRef.current);
        voiceSendTimerRef.current = null;
      }
      voiceFinalBufferRef.current = "";
    }
  }, [isVoiceTurnLocked, ENABLE_REALTIME_VOICE]);

  // 🧩 Hide Quick Actions if the latest assistant message requests it
  const shouldHideQuickActions = useMemo(() => {
    const lastAssistant = [...messages].reverse().find(m => m.sender === "assistant");
    return lastAssistant?.hideQuick === true;
  }, [messages]);


  const handlePickQuickAction = useCallback(
    (a: QuickAction & { enforce?: boolean }) => {
      resetActivitySafe();

      // Homework choices are complete answers.
      // Preserve the existing behavior of sending the visible number.
      if (a.group === "HomeworkSelect") {
        const num = (a.label ?? a.value ?? "").trim();

        if (num) {
          sendUiAction(num);
        }

        return;
      }

      // Coping-skill numbered choices are also complete answers.
      if (a.group === "CopingSelect") {
        const num = (a.label ?? a.value ?? "").trim();

        if (num) {
          sendUiAction(num);
        }

        return;
      }

      const value = (a.value ?? "").trim();
      if (!value) return;

      // Number-button SUDS remains a discrete one-click answer.
      // Slider / Gauge / Emoji do NOT come through this path;
      // they retain their explicit Submit button in SudsInput.
      const sudsLike = /^(?:[0-9]|10)$/.test(value);

      if (sudsLike) {
        setSudsEnforce(true);
      }

      // All QuickActionsBar buttons represent committed answers:
      // Finished, Ready, None, ALTERED choices, numeric SUDS, etc.
      sendUiAction(value);
    },
    [
      resetActivitySafe,
      sendUiAction,
    ]
  );

  const handleSudsControlSubmit = useCallback(
    (
      value: number,
      source: SudsInputSource
    ) => {
      resetActivitySafe();

      debugLog("[SUDS control] submit", {
        value,
        source,
        awaitKey: pendingAwaitKey,
      });

      // Slider / Gauge / Emoji keep their explicit Submit step.
      //
      // Once Submit is pressed, this is a committed UI answer:
      // send through the normal conversation path without stopping
      // an active realtime voice session.
      sendUiAction(String(value));
    },
    [
      resetActivitySafe,
      sendUiAction,
      pendingAwaitKey,
    ]
  );

  useEffect(() => {
    if (!displayActions.length) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const active = document.activeElement as HTMLElement | null;
      const isInput = active?.tagName?.toLowerCase() === "input";
      if (!isInput) return;

      const current = chatInputRef.current?.getMessage?.() ?? "";
      if (current.trim().length > 0) return;
      if ((e as any).isComposing) return;

      const key = e.key.toLowerCase();
      const match = displayActions.find(
        (qa) => qa.shortcut && qa.shortcut.toLowerCase() === key
      );
      if (match) {
        e.preventDefault();
        handlePickQuickAction(match);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [displayActions, handlePickQuickAction]);

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="border-b">
        <ChatHeader
          onOpenPracticeCalendar={() => setShowPracticeCalendar(true)}
          onAccessCodeChange={setAccessCode}
          onVoiceKeyChange={handleVoiceKeyChange}
          coins={coins}
          unlockedItems={unlockedItems}
          equippedItems={equippedItems}
          userId={profileUserId}
          nickname={nickname ?? undefined}
          avatarId={avatarId ?? undefined}
          onAvatarChange={setAvatarId}
          onCoinsChange={setCoins}
          onUnlockedItemsChange={setUnlockedItems}
          onEquippedItemsChange={setEquippedItems}
        />
      </div>

      <PracticeCalendarModal
        open={showPracticeCalendar}
        homework={practiceCalendarHomework}
        onClose={() => setShowPracticeCalendar(false)}
      />

      {showDashboard && (
        <>
          <BadgeRow
            rank={badgeLevel}
            pointsEarned={pointsEarned}
            coins={coins}
          />

          {badgeCollection.length > 0 && (
            <BadgeCollectionRow items={badgeCollection} />
          )}

          {initComplete && homeworkSummary && (
            <HomeworkSummaryRow
              summary={homeworkSummary}
              onNeedsAttentionClick={() =>
                setPrefillText("show homework due now")
              }
              onOpenHomeworkClick={() =>
                setPrefillText("What's my homework?")
              }
            />
          )}
        </>
      )}

      {showInitOverlay && <InitOverlay />}

      {!isMobile && showRewardOverlay && (
        <RewardOverlay
          key={lastCelebrationSigRef.current ?? "reward"}
          durationMs={getOutboundType() === "audio" ? REWARD_MS_AUDIO : REWARD_MS_TEXT}
          onDone={() => setShowRewardOverlay(false)}
        />
      )}

      <div className="mx-auto w-full max-w-3xl px-4 pt-3">
        <button
          onClick={() => {
            // Stop realtime voice and discard any transcript waiting to be sent.
            if (voiceSendTimerRef.current) {
              window.clearTimeout(voiceSendTimerRef.current);
              voiceSendTimerRef.current = null;
            }

            voiceFinalBufferRef.current = "";
            suspendRealtimeVoiceRef.current = false;

            if (ENABLE_REALTIME_VOICE && realtimeVoice.isActive) {
              realtimeVoice.stop();
            }

            if (ENABLE_GPT_LIVE_1 && gptLiveVoice.isConnected) {
              void gptLiveVoice.stop();
            }

            // Clear old GPT-Live continuation context for a genuinely new chat.
            gptLiveRehydrationContextRef.current = "";

            const newId =
              crypto.randomUUID?.() ||
              Math.random().toString(36).slice(2) + Date.now().toString(36);

            // always start a fresh session
            sessionStorage.setItem("sessionId", newId);
            sessionStorage.removeItem("didInit");
            setSessionId(newId);

            // ✅ NEW: make sure prior init UI state doesn't leak into the new chat
            setShowInitOverlay(false);
            setInitComplete(false);

            // reset chat state
            setPrefillText(null);
            setIsLoading(false);
            setInHomework(false);
            setPendingAwaitKey(null);
            setPendingSignals(null);
            setCopingPracticeFinishedMessageId(null);
            setHomeworkSummary(null);
            setPracticeCalendarHomework([]);
            setShowPracticeCalendar(false);
            lastAssistantIsQAStepRef.current = false;

            const code = (accessCode ?? "").trim();

            // ✅ NEW: if access code is missing, show a system prompt and lock input
            if (!code) {
              setAccessCodeValid(false);
              setInitError("❌ No access code set. Please enter an access code in the header to start.");

              setShowWelcome(false); // optional: avoids showing WelcomeCard when init can't run

              setMessages([
                {
                  id: String(Date.now()) + "-no-access",
                  text: "🔑 No access code set. Please enter an access code in the header to start.",
                  sender: "system",
                  timestamp: new Date(),
                },
              ]);

              return;
            }

            // access code exists → proceed normally
            setAccessCodeValid(true);
            setInitError(null);
            setShowWelcome(true);
            setMessages([]);
          }}

          className="inline-flex items-center gap-2 h-[42px] px-3 rounded-lg bg-white hover:bg-gray-100"
        >
          <SquarePen className="w-5 h-5" />
          <span>New chat</span>
        </button>

      </div>

      {/* Centered conversation container */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        style={{ WebkitOverflowScrolling: "touch" }}
        className={[
          "flex-1 relative",
          htmlActivityExpanded
            ? "overflow-visible"
            : "ios-chat-scroll overflow-y-scroll",
        ].join(" ")}
      >
        <div className="flex justify-center">
          <div className="w-full max-w-3xl px-4 space-y-4">
            {showWelcome &&
              initComplete &&
              messages.length === 0 && (
                <WelcomeCard
                  selfInitiatedPermitted={selfInitiatedPermitted ?? false}
                  nickname={nickname ?? undefined}
                  onPick={sendUiAction}
                />
              )}

            {messages.map((msg) => (
              <React.Fragment key={msg.id}>
                {msg.timer ? (
                  <>
                    <TimerBubble
                      ref={msg.timer.ended ? undefined : timerBubbleRef}
                      {...msg.timer}
                      announceTime={
                        !realtimeVoice.isActive &&
                        !realtimeVoice.isConnecting &&
                        !gptLiveVoice.isConnected &&
                        !gptLiveVoice.isConnecting
                      }
                      onEvent={handleTimerEvent}
                    />

                    {/* ✅ coaching tips directly from first OCD v4 response */}
                    {msg.sender === "assistant" &&
                      Array.isArray(msg.coachPrompts) &&
                      msg.coachPrompts.length > 0 && (
                        <CoachingTipsBubble prompts={msg.coachPrompts} />
                      )}
                  </>
                ) : msg.rewardGif ? (
                  // ✅ Mobile reward GIF row (inline)
                  <div className="flex justify-start">
                    <div className="mt-2 rounded-2xl overflow-hidden shadow-sm border bg-white">
                      <img
                        src={REWARD_GIF_SRC}
                        alt="Reward"
                        className={REWARD_GIF_WIDTH_CLASS}
                        loading="eager"
                        onLoad={() => {
                          if (isAtBottom) {
                            messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
                          }
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <MessageBubble message={msg} />
                )}

                {msg.sender === "assistant" &&
                  Array.isArray(msg.attachments) &&
                  msg.attachments.length > 0 && (
                    <div className="flex justify-start">
                      <div className="w-full min-w-0 max-w-[95%] space-y-2 sm:max-w-[85%]">
                        {msg.attachments.map((attachment, index) => (
                          <div
                            key={`${msg.id}-${attachment.type}-${index}`}
                            className="rounded-2xl border border-blue-200 bg-blue-50 p-4 shadow-sm"
                          >
                            <div className="break-all text-sm font-semibold text-slate-900">
                              {attachment.filename}
                            </div>

                            <a
                              href={attachment.downloadUrl}
                              download={attachment.filename}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-3 inline-flex min-h-11 max-w-full items-center justify-center rounded-xl bg-blue-600 px-4 py-2 text-center text-sm font-semibold text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                            >
                              {attachment.label}
                            </a>

                            {attachment.helperText && (
                              <p className="mt-2 text-xs leading-5 text-slate-600">
                                {attachment.helperText}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {msg.sender === "assistant" &&
                  msg.intent === "COPING_GUIDE" &&
                  msg.mode === "GUIDE" &&
                  msg.coachSkill ? (
                  <div className="mt-2 ml-0">
                    <CoachSkillWidget
                      ref={
                        msg.id === activeCoachSkillMessageId
                          ? coachSkillWidgetRef
                          : undefined
                      }
                      coachSkill={msg.coachSkill}
                      defaultAudioEnabled={
                        gptLiveVoice.isConnected ||
                        getOutboundType() === "audio"
                      }
                      onActivityEvent={handleCoachSkillActivityEvent}
                    />
                  </div>
                ) : null}

                {/* render-phase check (assistant-only) */}
                {DEBUG && msg.sender === "assistant" &&
                  (() => {
                    debugLog("🧪 render-phase artifact check", {
                      id: msg.id,
                      hasArtifact: !!msg.artifact,
                    });
                    return null;
                  })()
                }

                {msg.sender === "assistant" && msg.artifact ? (
                  <div className="mt-2 ml-0">
                    {msg.artifact.type === "html_activity" ? (
                      <StepHtmlActivityBubble
                        artifact={msg.artifact as any}
                        sessionId={sessionId}
                        accessCode={accessCode || undefined}
                        onFinished={() => {
                          sendUiAction("finished");
                        }}
                        onActivityEvent={handleCoachSkillActivityEvent}
                        onExpandedChange={setHtmlActivityExpanded}
                      />
                    ) : msg.artifact.type === "expectancy_tracker" ? (
                      <StepExpectancyTrackerBubble
                        artifact={msg.artifact as any}
                      />
                    ) : msg.artifact.type === "abc_game" ? (
                      <StepAbcGameBubble
                        artifact={msg.artifact as any}
                        onAudioResponse={handleWidgetAudioResponse}
                      />
                    ) : msg.artifact.type === "target_game" ? (
                      <StepTargetGameBubble
                        artifact={msg.artifact as any}
                        onAudioResponse={handleWidgetAudioResponse}
                      />
                    ) : msg.artifact.type === "coping_buddy" ? (
                      <StepCopingBuddyBubble
                        ref={
                          msg.id === latestAssistant?.id
                            ? copingBuddyRef
                            : undefined
                        }
                        artifact={msg.artifact as any}
                        onActivityEvent={handleCoachSkillActivityEvent}
                        onAudioResponse={handleWidgetAudioResponse}
                        onHtmlActivityExpandedChange={setHtmlActivityExpanded}
                        onAbcGameMessage={handleAbcGameMessage}
                        onTargetGameMessage={handleTargetGameMessage}
                        gptLiveActive={gptLiveVoice.isConnected}
                      />
                    ) : msg.artifact.type === "learning_buddy" ? (
                      <StepLearningBuddyBubble
                        ref={
                          msg.id === latestAssistant?.id
                            ? learningBuddyRef
                            : undefined
                        }
                        artifact={msg.artifact as any}
                        onActivityEvent={handleCoachSkillActivityEvent}
                        onHtmlActivityExpandedChange={setHtmlActivityExpanded}
                      />
                    ) : msg.artifact.type === "imaginary_handwashing" ? (
                      <StepImaginaryHandwashingBubble
                        artifact={msg.artifact as any}
                        sessionId={sessionId}
                        accessCode={accessCode || undefined}
                        onFinished={() => {
                          sendUiAction("finished");
                        }}
                      />
                    ) : (
                      <StepUploadBubble
                        artifact={msg.artifact as any}
                        sessionId={sessionId}
                        accessCode={accessCode || undefined}
                        db={runtimeDb}
                        onUploaded={() => { }}
                      />
                    )}
                  </div>
                ) : null}

                {msg.sender === "assistant" &&
                  msg.id === latestAssistant?.id &&
                  shouldDelayCopingLoopSuds ? (
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => {
                        resetActivitySafe();

                        // Local UI transition only.
                        // n8n is already waiting for coping_loop_suds.
                        setCopingPracticeFinishedMessageId(msg.id);

                        // After the configured SUDS input appears in the composer,
                        // move gently to the bottom on smaller screens.
                        requestAnimationFrame(() => {
                          requestAnimationFrame(() => {
                            messagesEndRef.current?.scrollIntoView({
                              behavior: "smooth",
                              block: "end",
                            });
                          });
                        });
                      }}
                      className="inline-flex min-h-11 w-full items-center justify-center rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    >
                      Done practicing
                    </button>
                  </div>
                ) : null}
              </React.Fragment>
            ))}

            {isLoading && <TypingIndicator />}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {!isAtBottom && (
          <button
            onClick={() =>
              messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
            }
            className="absolute bottom-4 right-4 bg-gray-700 text-white p-2 rounded-full shadow-lg hover:bg-gray-600"
          >
            ↓
          </button>
        )}
      </div>

      {/* Centered input row */}
      <div className="sticky bottom-0 bg-white border-t">
        <div className="flex justify-center">
          <div className="w-full max-w-3xl px-4 py-4">

            {!showInitOverlay &&
              !isLoading &&
              !hasVisibleTimer &&
              activeSudsControl &&
              hasCustomSudsControl &&
              !shouldDelayCopingLoopSuds && (
                <div className="mb-3">
                  <SudsInput
                    key={`${latestAssistant?.id ?? "suds"}:${activeSudsControl.awaitKey}:${activeSudsControl.style}`}
                    control={activeSudsControl}
                    disabled={
                      !initComplete ||
                      !accessCodeValid
                    }
                    onSubmit={handleSudsControlSubmit}
                  />
                </div>
              )}

            {!showInitOverlay &&
              !isLoading &&
              displayActions.length > 0 &&
              !hasVisibleTimer &&
              !suppressQuickReplies &&
              (
                !shouldHideQuickActions ||
                showCopingLoopSudsNumberButtons
              ) && (
                <QuickActionsBar
                  actions={displayActions}
                  onPick={handlePickQuickAction}
                  sudsContext={{
                    awaitKey: latestAssistant?.isQAStep ? null : pendingAwaitKey,
                    mode: latestAssistant?.mode,
                  }}
                />
              )}

            <ChatInput
              ref={chatInputRef}
              key={sessionId}
              onSendMessage={handleSendMessage}
              isLoading={isLoading}
              isInitializing={showInitOverlay}
              accessCode={accessCode}
              sessionId={sessionId}
              prefillText={prefillText ?? undefined}
              onClearPrefill={() => setPrefillText(null)}
              sudsMode={sudsEnforce && !latestAssistant?.isQAStep}
              homeworkSelectMode={isHomeworkChoice}   // 👈 add this line
              disabled={
                showInitOverlay ||
                !initComplete ||
                hasVisibleTimer ||
                !accessCodeValid ||
                shouldDelayCopingLoopSuds
              }
              realtimeVoice={
                ENABLE_GPT_LIVE_1
                  ? {
                    supported: gptLiveVoice.supported,
                    isActive: gptLiveVoice.isConnected,
                    isConnecting: gptLiveVoice.isConnecting,
                    start: gptLiveVoice.start,
                    stop: gptLiveVoice.stop,
                  }
                  : ENABLE_REALTIME_VOICE
                    ? realtimeVoice
                    : null
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatContainer;
